import { datadogRum } from '@datadog/browser-rum'
import { get as _get, set as _set } from 'lodash'
import React, { Component } from 'react'
import {
  ConfigurationDetail,
  DatadogEncodingRun,
  EncodingCounters,
  EncodingOperationToPerform,
  EncodingProductionOrderRows,
  Encodings,
  EncodingValidationRequest,
  EncodingValidationResponse,
  IdentifierType,
  ProductionOrder,
  TmrTag,
  WorkOrder
} from 'stylewhere/api'
import {
  Box,
  Button,
  EncodingForm,
  EncodingIdentifers,
  EncodingProduct,
  EncodingProductionOrderDetail,
  EncodingReading,
  EncodingRightHeader,
  EncodingUserCounters,
  FeedbackModal,
  FullLoadingLayer,
  InputModal,
  Page,
  Text
} from 'stylewhere/components'
import type { EncodingFormState } from 'stylewhere/components/encoding/EncodingForm'
import { EncodingExtensions } from 'stylewhere/extensions'
import {
  AppStore,
  FormSchemaData,
  getDataFromSchema,
  OperationReadingProps,
  RemoteOperation,
  RfidReader,
  Router,
  Sounds
} from 'stylewhere/shared'
import { __, T } from 'stylewhere/shared/i18n'
import { EncodingOperationConfig } from 'stylewhere/shared/RemoteOperation'
import {
  askUserConfirmation,
  BLOCKED_ERRORS,
  encodingComputeTagsEntityCode,
  getAskUserInputEveryTime,
  getAutomaticRestartAntenna,
  getCanForceAssocation,
  getDesiredCode,
  getEnableUhfWrite,
  getEncodedOrder,
  getEncodingAssociationStatus,
  getEncodingPageLayout,
  getEncodingProductFields,
  getEncodingTagPassword,
  getInitialType,
  getStartAntennaOrder,
  getTagType,
  getTimeoutValidateTags,
  getUhfTagToWriteRole,
  getUseTidTargetDuringWrite,
  getWithUserConfirmationMessage,
  isCanEncodeWithOverQuantity,
  isCheckEncodedQuantity,
  isExpectOnlyMandatoryIdentifiers,
  isModalError,
  isOrderCompleted,
  isOrderRowCompleted,
  getScanIdentifierConfig,
  isSkipValidation,
  isSuccessEncodingCreate,
  isValidOperationEncodingTagType,
  MAX_WRITE_ATTEMPT,
  showToast,
  showToastError,
  sleep,
  uuid,
  encodingStartAntennaAfterSimpleItemIdentifier,
  isPreEncodingWorkOrder,
  getLockTagOnWriteFailure,
  getCheckTagAfterWrite
} from 'stylewhere/shared/utils'
import {
  getNfcReadConfiguration,
  getNfcTagReadErrors,
  isNfcAuthEnabled,
  readNfcAuthenticatedTag
} from 'stylewhere/shared/nfcAuthentication'
import { OperationEncodingTagType, OperationUserSetting } from 'stylewhere/types'

export interface EncodingState {
  encodingValidation?: EncodingValidationRequest
  encodingValidationResponse?: EncodingValidationResponse
  encodingCreateResponse?: EncodingValidationResponse
  formSchemaData?: FormSchemaData
  options: {
    value: string
    label: string
    active: boolean
  }[]
  counters?: EncodingCounters
  tagsRead: TmrTag[]
  processing: boolean | 'nfcDecryption'
  associationStatus: 'TO_BE_READ' | 'CONFIRMED' | 'ERROR' | 'PROCESSING' | 'IGNORED' | 'SKIPPED' | 'WRITING'
  showPin: boolean
  antennaOn: boolean
  forceAssociation: boolean
  pin: string
  configuration: any[]
  encodingCreateCounter: number
  targetTag: string
  targetTid: string
  tagToWrite: string
  serial: string
  numWriteAttempt: number
  writeRetryCount: number
  starting: boolean
  resetFormCounter: number
  currentTagType: OperationEncodingTagType
  startedTagType: OperationEncodingTagType
  showProductionOrder: boolean
  enableOkSounds: boolean
  enableKoSounds: boolean
  orderFound: any
  showUserCounters: boolean
}

export default class EncodingEncode<
  P extends OperationReadingProps<EncodingState>,
  S extends EncodingState
> extends Component<P, S> {
  lastSerial
  get matchParams() {
    return Router.getMatchParams(this.props)
  }
  get operation() {
    return RemoteOperation.getOperationConfig<EncodingOperationConfig>(this.matchParams.opCode)
  }
  get initialType() {
    return getInitialType(this.operation)
  }
  datadogSession: DatadogEncodingRun | undefined = undefined
  encodingFormRef = React.createRef<EncodingForm<EncodingFormState>>()
  get tagTypesToCheck() {
    return getStartAntennaOrder(this.operation)
  }

  getInitFormSchemaData = () => {
    if (
      this.initialType === 'formSchema' &&
      this.operation.formSchema &&
      Object.keys(this.operation.formSchema).length > 0
    ) {
      return {}
    }
    if (this.initialType === 'workOrder') {
      return {}
    }
    return { product: { code: '' }, wam: '' }
  }

  public state: S = {
    options: [
      { value: 'associate', label: __(T.misc.associate), active: true },
      { value: 'verify', label: __(T.misc.verify_association), active: false }
    ],
    formSchemaData: this.getInitFormSchemaData(),
    tagsRead: [],
    processing: false,
    associationStatus: 'TO_BE_READ',
    showPin: false,
    antennaOn: false,
    pin: '',
    forceAssociation: false,
    configuration: [],
    encodingCreateCounter: 0,
    targetTag: '',
    targetTid: '',
    tagToWrite: '',
    serial: '',
    numWriteAttempt: 0,
    writeRetryCount: 0,
    starting: false,
    resetFormCounter: 1,
    currentTagType: this.tagTypesToCheck[0],
    startedTagType: 'ALL',
    showProductionOrder: false,
    enableOkSounds: true,
    enableKoSounds: true,
    orderFound: undefined,
    showUserCounters: false
  } as any

  isEncoding = false
  isDecodeNfc = false
  encodingStart
  isModal = false
  timer: NodeJS.Timeout | null = null
  timerReader?: any
  timerTags: NodeJS.Timeout | null = null
  timerNoRead: NodeJS.Timeout | null = null
  antennaStartTime?: Date
  writeTagStartTime?: Date
  totalWriteTagTime?: number

  findOrderTags: { code: string; processed: boolean }[] = []
  findOrderInProgress = false
  counterFindOrderTag = 0

  isIdentifierFlow = () => {
    return ['identifier', 'identifierOrderRow'].includes(this.initialType)
  }

  componentDidMount() {
    this.isModal = isModalError(this.operation)
    if (this.operation.options.showOnlyVerify === 'true') {
      Router.navigate(`/encoding/:opCode/verify`, { opCode: this.operation.code }, { replace: true })

      globalThis.location.reload()
      return
    }
    void this.initializeOnMount()
  }

  initializeOnMount = async () => {
    if (this.isIdentifierFlow()) {
      if (isSkipValidation(this.operation)) {
        //get itemConfiuguration
        const config = await EncodingExtensions.getItemTypeConfiguration(this.operation)
        if (config) {
          const encodingValidation: EncodingValidationRequest = {
            identifiers: [],
            operationId: this.operation.id,
            placeId: AppStore.defaultWorkstation?.placeId
          }
          this.setState(
            { configuration: config, forceAssociation: true, encodingValidation: encodingValidation },
            this.initializeReader
          )
        } else {
          this.showToastErrorWithDatadog(__(T.error.no_item_configuration), __(T.error.error), this.isModal)
        }
      } else {
        this.setState({ starting: true }, this.initializeReader)
      }
    } else {
      this.initializeReader()
    }
  }

  getAntennaOffsetMs = (fromOperationStart = false) => {
    const antennaStartMs = this.antennaStartTime?.getTime()
    const sessionStartMs = this.datadogSession?.startTime?.getTime()
    const startMs = fromOperationStart ? sessionStartMs : (antennaStartMs ?? sessionStartMs)
    return startMs ? Date.now() - startMs : 0
  }

  getDatadogErrorsPayload = (errors: any[] = []) => {
    return errors.map((error) => ({
      error: error?.errorCode ?? error?.message ?? 'UNKNOWN_ERROR',
      details: error,
      offsetMs: error?.offsetMs ?? Date.now() - this.datadogSession?.startTime?.getTime()
    }))
  }

  trackDatadogAction = (action: string, additionalInfo?: Record<string, any>, errors?: any[]) => {
    if (!this.datadogSession) return
    if (AppStore.getEmulation()) return
    this.cleanDataDogSessionDeviceManager()
    const payload: DatadogEncodingRun = {
      ...this.datadogSession
    }
    if (additionalInfo) {
      payload.additionalInfo = {
        ...this.datadogSession.additionalInfo,
        ...additionalInfo
      }
    }
    if (errors && errors.length > 0) {
      if (!this.datadogSession.errors) this.datadogSession.errors = []
      this.datadogSession.errors = [...this.datadogSession.errors, ...this.getDatadogErrorsPayload(errors)]
      payload.errors = this.datadogSession.errors
    }
    datadogRum.addAction(action, payload)
  }

  trackValidationErrors = (errors: any[] = []) => {
    const filteredErrors = errors.filter(
      (error) => !['MISSING_OPTIONAL_TAG', 'MISSING_MANDATORY_TAG'].includes(error?.errorCode)
    )
    if (filteredErrors.length > 0) {
      void this.trackEncodingError({ actionname: 'validateError', errors: filteredErrors })
    }
  }

  trackMissingTagsTimeout = (errors: any[] = []) => {
    const missingErrors = errors.filter((error) =>
      ['MISSING_OPTIONAL_TAG', 'MISSING_MANDATORY_TAG'].includes(error?.errorCode)
    )
    if (missingErrors.length > 0) {
      void this.trackEncodingError({ actionname: 'MISSING_TAG_TIMEOUT', errors: errors })
    }
  }

  trackForcibleErrorsModalClick = (errors: any[] = []) => {
    this.trackDatadogAction('forcibleErrorsModalClick', { errorCount: errors.length }, errors)
  }

  trackReadEpcMismatch = (requestedEpc: string, readEpc: string | undefined) => {
    if (!readEpc || readEpc === requestedEpc) return
    this.trackDatadogAction('readEpcMismatch', { requestedEpc, readEpc })
  }

  trackEncodingError = async ({
    actionname,
    message,
    errors,
    context
  }: {
    actionname?: string
    message?: string
    errors?: any[]
    context?: Record<string, any>
  }) => {
    if (!this.datadogSession) {
      this.initializeDataDogSession(this.state.formSchemaData)
    }
    const safeMessage = message ?? errors?.[0]?.errorCode ?? 'Unknown error'
    this.trackDatadogAction(actionname ?? 'encodingError', { message: safeMessage, ...context }, errors)
  }

  ensureDataDogSession = (formSchemaData?: FormSchemaData) => {
    if (!this.datadogSession) {
      this.initializeDataDogSession(formSchemaData ?? this.state.formSchemaData)
    }
  }

  onWorkOrderDatadogEvent = (action: string, context?: Record<string, unknown>) => {
    this.ensureDataDogSession(
      context?.workOrder ? ({ workOrder: context.workOrder } as FormSchemaData) : this.state.formSchemaData
    )
    this.trackDatadogAction(action, context)
  }

  showToastErrorWithDatadog = (
    errorOrMessage: any,
    title: string,
    isModal?: boolean,
    options?: {
      onClose?: () => void
      okText?: string
      enableKoSounds?: boolean
      errors?: any[]
      context?: Record<string, any>
      datadogAction?: string
    }
  ) => {
    const message = errorOrMessage?.message ?? errorOrMessage
    void this.trackEncodingError({
      actionname: options?.datadogAction,
      message: message,
      errors: options?.errors,
      context: options?.context
    })
    showToastError(errorOrMessage, title, isModal, options?.onClose, options?.okText, options?.enableKoSounds)
  }

  executeReadEpcWithMetrics = async (requestedEpc: string) => {
    const result = await RfidReader.readEpc(requestedEpc, this.datadogSession, 'readEpcDeviceManager')
    this.trackReadEpcMismatch(requestedEpc, result.value)
    return result
  }

  getAntennaCustomCommands = () => {
    return EncodingExtensions.getAntennaCustomCommandsOperationType(this.operation)
  }

  startAntenna = async (tagTypeInput?: OperationEncodingTagType) => {
    const { currentTagType } = this.state
    let tagType: OperationEncodingTagType = currentTagType
    if (isValidOperationEncodingTagType(tagTypeInput)) {
      tagType = tagTypeInput
    }

    if (!RfidReader.isReading()) {
      this.setState({ starting: true, currentTagType: tagType })

      //reset automatic stop antenna
      RfidReader.setAutomaticStop(this.operation.autostopAntennaTimeout > 0)
      RfidReader.setAvoidRestartingTimerOnRead(this.operation.avoidRestartingTimerOnRead)
      RfidReader.setNfcReadConfiguration(getNfcReadConfiguration(this.operation))

      // start antenna with automatic restart by option
      await RfidReader.start({
        restart: getAutomaticRestartAntenna(this.operation),
        tagTypeToCheck: tagType,
        customCommandsOperationType: this.getAntennaCustomCommands()
      })
    }
  }

  stopAntenna = async () => {
    if (RfidReader.isReading()) {
      this.setState({ starting: true })
      await RfidReader.stop({ customCommandsOperationType: this.getAntennaCustomCommands() })
    }
  }

  updateFormSchemaData = (formSchemaData: FormSchemaData) => {
    this.setState({ formSchemaData })
  }

  initializeDataDogSession = (formSchemaData?: FormSchemaData) => {
    const isActive = AppStore.isDatadogActive()
    const safeFormData = formSchemaData ?? this.state.formSchemaData ?? {}
    if (isActive && !AppStore.getEmulation()) {
      this.antennaStartTime = undefined
      this.datadogSession = {
        runId: uuid().toString(),
        operationCode: this.operation.code,
        startTime: new Date(),
        formData: safeFormData,
        initialTypeRequested: {
          label: EncodingExtensions.getDataDogFormSchemaLabel(this.operation, safeFormData, this.initialType),
          type: this.initialType,
          offsetMs: 0
        },
        errors: []
      }
      this.trackDatadogAction('startEncoding')
    }
  }

  cleanDataDogSessionDeviceManager = () => {
    if (this.datadogSession?.deviceManager) {
      delete this.datadogSession.deviceManager
    }
  }

  getUpdatedCounters = (success) => {
    const { formSchemaData } = this.state
    const tmp = this.state.counters
    if (tmp && success) {
      if (tmp.parent) {
        tmp.parent.encoded = (tmp.parent.encoded ?? 0) + 1
      }
      if (tmp.order) {
        tmp.order.encoded = (tmp.order.encoded ?? 0) + 1
      }
      if (tmp.row) {
        tmp.row.encoded = (tmp.row.encoded ?? 0) + 1
      }
    }
    if (formSchemaData.wordOrderRowId && tmp.rows) {
      const find = tmp.rows.find((row) => row.rowId === formSchemaData.wordOrderRowId)
      if (find) {
        find.encoded = (find.encoded ?? 0) + 1
      }
    }
    return tmp
  }

  getInitialTypeFormSchema = () => {
    const schema = EncodingExtensions.formSchema(this.operation)
    const isOrderFormSchema = !!(this.initialType === 'formSchema' && schema.some((el) => el.initialType === 'order'))
    const isOrderRowFormSchema = !!(
      this.initialType === 'formSchema' && schema.some((el) => el.initialType === 'orderRow')
    )
    return {
      order: isOrderFormSchema,
      orderRow: isOrderRowFormSchema
    }
  }

  onSubmitForm = async (formSchemaData: FormSchemaData) => {
    const { resetFormCounter } = this.state
    const formSchemaType = this.getInitialTypeFormSchema()
    try {
      this.initializeDataDogSession(formSchemaData)
      const requestStart = Date.now()
      const res = await EncodingExtensions.getItemConfiguration(
        this.operation,
        formSchemaData,
        undefined,
        this.datadogSession?.runId
      )
      const apiDurationMs = Date.now() - requestStart
      this.trackDatadogAction('getItemConfiguration', { apiDurationMs: apiDurationMs })
      if (
        (['order', 'orderRow'].includes(this.initialType) || formSchemaType.order || formSchemaType.orderRow) &&
        isOrderRowCompleted(
          res.counters,
          isCheckEncodedQuantity(this.operation),
          isCanEncodeWithOverQuantity(this.operation)
        )
      ) {
        this.showToastErrorWithDatadog(__(T.error.production_order_row_completed), __(T.error.error), this.isModal)
        const formSchema: any = this.getInitFormSchemaData()
        if (this.initialType === 'order' || formSchemaType.order) {
          formSchema.productionOrder = this.state.formSchemaData.productionOrder
        }
        this.setState({ formSchemaData: formSchema, resetFormCounter: resetFormCounter + 1 })
      } else if (
        this.initialType === 'workOrder' &&
        isOrderRowCompleted(
          res.counters,
          isCheckEncodedQuantity(this.operation),
          isCanEncodeWithOverQuantity(this.operation)
        )
      ) {
        this.showToastErrorWithDatadog(__(T.error.work_order_row_completed), __(T.error.error), this.isModal, {
          datadogAction: 'workOrderRowCompleted',
          context: {
            workOrder: formSchemaData.workOrder,
            workOrderRow: formSchemaData.workOrderRow
          }
        })
        const formSchema: any = this.getInitFormSchemaData()
        formSchema.workOrder = this.state.formSchemaData.workOrder
        this.setState({ formSchemaData: formSchema, resetFormCounter: resetFormCounter + 1 })
      } else {
        this.setState(
          {
            encodingValidation: res.encodingValidation,
            encodingValidationResponse: res.encodingValidationResponse,
            counters: res.counters,
            formSchemaData
          },
          this.checkStartReaderAfterItemConfiguration
        )
      }
    } catch (error) {
      this.setState({ resetFormCounter: resetFormCounter + 1 })
      this.showToastErrorWithDatadog(error, __(T.error.error), this.isModal, {
        enableKoSounds: this.state.enableKoSounds
      })
    }
  }

  isRequestedSimpleItemIdentifier = () => {
    const { encodingValidationResponse } = this.state
    const configuration = encodingValidationResponse?.configuration?.details ?? []
    const checkSimple = configuration.find((s) => s.identifierType === 'SIMPLE_ITEM_IDENTIFIER' && !s.optional)
    return checkSimple
  }

  checkStartReaderAfterItemConfiguration = () => {
    if (!encodingStartAntennaAfterSimpleItemIdentifier() || !this.isRequestedSimpleItemIdentifier()) {
      this.startReader()
    }
  }

  onProductionOrderReceived = async (productionOrder: ProductionOrder) => {
    try {
      const countersResponse = await Encodings.getProductionOrderCounter(productionOrder.id)
      this.setState({ counters: countersResponse?.data }, this.checkProductionOrder)
    } catch (error) {
      this.showToastErrorWithDatadog(error, __(T.error.error), this.isModal)
    }
  }

  onWorkOrderReceived = async (workOrder: WorkOrder) => {
    try {
      const res = await Encodings.getWorkOrderCounter(workOrder.id)
      this.setState({ counters: res?.data }) // check for work order completion is done before, in EncodingForm.submitWorkOrder
    } catch (error) {
      this.showToastErrorWithDatadog(error, __(T.error.error), this.isModal)
    }
  }

  checkProductionOrder = () => {
    const { counters } = this.state
    if (
      isOrderCompleted(counters, isCheckEncodedQuantity(this.operation), isCanEncodeWithOverQuantity(this.operation))
    ) {
      this.showToastErrorWithDatadog(__(T.error.production_order_completed), __(T.error.error), this.isModal)
    }
  }

  getIgnoreUnknownTags = () => {
    const { options } = this.operation
    return options?.ignoreUnknownTags && options.ignoreUnknownTags === 'yes'
  }

  onBeforeInitializeReader() {
    // extensible
  }

  initializeReader = async () => {
    try {
      const scanIdentifierConfig = getScanIdentifierConfig(this.operation)

      this.onBeforeInitializeReader()
      await RfidReader.initialize(this.getAntennaCustomCommands())
      RfidReader.setAutomaticStop(this.operation.autostopAntennaTimeout > 0)
      RfidReader.setAutomaticStopTime(this.operation.autostopAntennaTimeout)
      RfidReader.setAvoidRestartingTimerOnRead(this.operation.avoidRestartingTimerOnRead)
      RfidReader.setIgnoreReadingTagsWithPrefixes(
        this.operation.options?.ignoreReadingTagsWithPrefixes
          ? this.operation.options.ignoreReadingTagsWithPrefixes
          : ''
      )
      RfidReader.setOnTagReadCallback(this.isIdentifierFlow() ? this.onTagReadIdentifier : this.onTagRead)
      RfidReader.onStartCallback = this.onStartCallback
      RfidReader.onStopCallback = this.onStopCallback
      if (
        this.isIdentifierFlow() &&
        scanIdentifierConfig.rfid &&
        this.operation.options.disableAutomaticStart !== 'true'
      ) {
        await this.startReader()
      } else if (
        this.isIdentifierFlow() &&
        scanIdentifierConfig.rfid &&
        this.operation.options.disableAutomaticStart === 'true'
      ) {
        this.setState({ starting: false })
      }
    } catch (error) {
      if (!AppStore.getEmulation()) {
        this.showToastErrorWithDatadog(
          (error as any).message ?? __(T.error.rfid_reader_initialization),
          __(T.error.error),
          this.isModal
        )
      }
    }
  }

  startReader = async () => {
    const { currentTagType } = this.state
    try {
      //reset automatic stop antenna
      RfidReader.setAutomaticStop(this.operation.autostopAntennaTimeout > 0)
      RfidReader.setAutomaticStopTime(this.operation.autostopAntennaTimeout)
      RfidReader.setAvoidRestartingTimerOnRead(this.operation.avoidRestartingTimerOnRead)
      RfidReader.setNfcReadConfiguration(getNfcReadConfiguration(this.operation))

      // start antenna with automatic restart by option
      await RfidReader.start({
        restart: getAutomaticRestartAntenna(this.operation),
        tagTypeToCheck: currentTagType,
        customCommandsOperationType: this.getAntennaCustomCommands()
      })
    } catch (error) {
      if (!AppStore.getEmulation()) {
        this.showToastErrorWithDatadog(
          (error as any).message ?? __(T.error.rfid_reader_initialization),
          __(T.error.error),
          this.isModal
        )
      }
    }
  }

  onStartCallback = () => {
    const { currentTagType } = this.state
    if (!this.isIdentifierFlow()) {
      if (!this.antennaStartTime) this.antennaStartTime = new Date()
      if (this.datadogSession) {
        if (this.datadogSession.startAntennaRequested) {
          this.datadogSession.startAntennaRequested.restartCount =
            (this.datadogSession.startAntennaRequested.restartCount ?? 0) + 1
        } else {
          this.datadogSession.startAntennaRequested = {
            antennaTagType: currentTagType,
            offsetFromAntennaStart: 0,
            offsetMs: this.getAntennaOffsetMs(true),
            restartCount: 0
          }
        }
        this.trackDatadogAction('antennaStart')
      }
    }
    this.setState({ antennaOn: true, starting: false, startedTagType: currentTagType })
    if (this.isIdentifierFlow()) {
      const timer = this.operation.autostopAntennaTimeout ?? 0
      if (timer > 0) {
        this.clearTimerNoReads()
        this.timerNoRead = setTimeout(async () => {
          await this.stopAntenna()
          this.feedbackSoundKo()
          void this.trackEncodingError({ actionname: 'timeoutTagsNoReads' })
          this.showToastErrorWithDatadog(__(T.error.timeout_no_reads), __(T.error.error), this.isModal, {
            onClose: () => {
              void this.onClear()
            }
          })
          this.showToastErrorWithDatadog(__(T.error.timeout_no_reads), __(T.error.error), this.isModal, {
            onClose: () => {
              void this.onClear()
            }
          })
        }, timer)
      }
    }
  }

  onStopCallbackIdentifierCheck(tmpEncoding: EncodingValidationRequest) {
    return tmpEncoding?.identifiers && !tmpEncoding.identifiers.every((idt) => idt._status === 'CONFIRMED')
  }

  onStopCallback = (automatic = false) => {
    const { currentTagType, startedTagType, associationStatus: currentAssociationStatus } = this.state
    if (automatic) {
      const stopErrors = this.state.encodingValidationResponse?.errors ?? []
      this.trackMissingTagsTimeout(stopErrors)
    }
    const IGNORE_EMULATION = false
    if (IGNORE_EMULATION || !AppStore.getEmulation()) {
      const tmpEncoding = this.state.encodingValidation
      if (!this.isIdentifierFlow()) {
        RfidReader.setAutomaticStop(false)
        RfidReader.stopTimer()
      }

      if (tmpEncoding && this.onStopCallbackIdentifierCheck(tmpEncoding)) {
        if (
          this.state.encodingValidationResponse.errors?.length > 0 &&
          this.state.encodingValidationResponse.errors.some((e) => e.errorCode === 'MISSING_MANDATORY_TAG')
        ) {
          this.feedbackSoundKo()
        }

        if (currentTagType === 'ALL') {
          tmpEncoding.identifiers
            .filter((id) => id.identifierType !== 'SIMPLE_ITEM_IDENTIFIER')
            .filter((id) => !id._read)
            .filter((id) => !['ERROR', 'NFC_COLLISION', 'SKIPPED', 'CONFIRMED'].includes(id._status))
            .forEach((idf) => {
              idf._status = 'ERROR'
              idf._error = __(T.imbustatrice.dynamic_tag_missing, { role: idf.role })
            })
          this.setState({
            antennaOn: false,
            starting: false,
            associationStatus: 'ERROR',
            encodingValidation: tmpEncoding
          })
        } else {
          // gestione multi tag type
          // quando arriva lo stop per un timeout deve:
          // 1. segnare l'errore nel tipo di tag di cui ho fatto start
          // 1a. devo ignorare gli errori della stop che ho dato per passare al tipo di tag successivo
          // 2. passare al prossimo tipo di tag

          const identifiersInTimeout = tmpEncoding.identifiers.filter(
            (id) =>
              ((id.identifierType === 'UHF_TAG' && startedTagType === 'UHF') ||
                (id.identifierType === 'NFC_TAG' && startedTagType === 'NFC')) &&
              !id._read
          )

          identifiersInTimeout.forEach((idf) => {
            idf._status = 'ERROR'
            idf._error = __(T.imbustatrice.dynamic_tag_missing, { role: idf.role })
          })

          // set next tag type if called by automatic stop
          if (automatic) {
            const nextTagType = this.getNextTagType()

            if (nextTagType) {
              this.setState(
                {
                  antennaOn: false,
                  starting: false,
                  associationStatus: identifiersInTimeout.length > 0 ? 'ERROR' : currentAssociationStatus,
                  encodingValidation: tmpEncoding,
                  currentTagType: nextTagType,
                  startedTagType: 'ALL'
                },
                async () => {
                  await this.startAntenna(nextTagType)
                }
              )
            } else {
              this.setState({
                antennaOn: false,
                starting: false,
                associationStatus: 'ERROR',
                encodingValidation: tmpEncoding,
                currentTagType: 'ALL',
                startedTagType: 'ALL'
              })
            }
          } else {
            this.setState({
              antennaOn: false,
              starting: false,
              encodingValidation: tmpEncoding
            })
          }
        }
      } else {
        this.setState({ antennaOn: false, starting: false })
      }
    } else {
      this.setState({ antennaOn: false, starting: false })
    }
  }

  clearTimerReader = () => {
    if (this.timerReader) {
      clearTimeout(this.timerReader)
      this.timerReader = null
    }
  }

  timeoutAutomaticReaderStop = async () => {
    if (this.isEncoding) return
    await this.stopAntenna()
    if (this.checkIfForceCreate('yes')) {
      this.call_force_encoding(false)
    } else if (this.checkIfForceCreate('withUserConfirmation')) {
      this.call_encoding_with_confirmation(false)
    } else {
      this.isEncoding = true
      this.encodingStart = new Date()
      this.setState(
        (prevState) => ({
          encodingCreateCounter: prevState.encodingCreateCounter + 1
        }),
        this.create
      )
    }
  }

  attachAutomaticReaderStop = () => {
    if (this.operation.autostopAntennaTimeout > 0) {
      this.clearTimerReader()
      this.timerReader = setTimeout(this.timeoutAutomaticReaderStop, this.operation.autostopAntennaTimeout)
    }
  }

  componentWillUnmount() {
    this.setState({ processing: false })
    this.clearTimerNoReads()
    this.clearTimerTags()
    this.clearTimerReader()
    this.clearTimer()
    RfidReader.onStartCallback = undefined
    RfidReader.onStopCallback = undefined
    void this.stopAntenna()
  }

  verifySerial = async (tag: TmrTag) => {
    this.lastSerial = tag
    this.setState({ processing: true }, this.executeVerify)
  }

  executeVerify = async () => {
    try {
      const tmpEncodingValidation: EncodingValidationRequest = {
        identifiers: [],
        operationId: this.operation.id,
        placeId: AppStore.defaultWorkstation?.placeId
      }
      await EncodingExtensions.onTagRead(tmpEncodingValidation, this.lastSerial, this.operation)
      const encode = await EncodingExtensions.verify(tmpEncodingValidation, false, this.datadogSession?.runId)
      if (encode?.encodingValidationResponse?.item?.identifiers) {
        const configuration = encode.encodingValidationResponse.configuration?.details ?? []
        const checkUhf = configuration.find((e) => e.identifierType === 'UHF_TAG')
        if (
          this.initialType === 'identifier' &&
          this.operation.options?.scanIdentifier === 'serial' &&
          (!checkUhf || checkUhf.optional)
        ) {
          const expectOnlyMandatoryIdentifiers = isExpectOnlyMandatoryIdentifiers(this.operation)
          configuration.forEach((configDetail) => {
            if (
              !tmpEncodingValidation.identifiers.some(
                (t) => t.identifierType === configDetail.identifierType && t.role === configDetail.role
              ) &&
              (!expectOnlyMandatoryIdentifiers || (expectOnlyMandatoryIdentifiers && !configDetail.optional))
            ) {
              tmpEncodingValidation.identifiers.push({ ...configDetail })
            }
          })
          this.setState(
            {
              processing: false,
              encodingValidation: tmpEncodingValidation,
              encodingValidationResponse: encode.encodingValidationResponse
            },
            this.startReader
          )
        } else {
          const idt = encode.encodingValidationResponse.item.identifiers.find((e) => e.type === 'UHF_TAG')
          if (idt) {
            this.lastSerial = null
            const tmpTag = { type: 'UHF_TAG', epc: idt.code }
            tmpEncodingValidation.identifiers = []
            this.setState(
              (prevState) => ({
                encodingValidation: tmpEncodingValidation,
                tagsRead: [...prevState.tagsRead, tmpTag as TmrTag]
              }),
              this.validateIdentifiersTag
            )
          } else {
            this.setState({ processing: false })
            this.removeTagReadFromReader([this.lastSerial])
            this.lastSerial = null
            this.showToastErrorWithDatadog('Unknown error during encoding validation', __(T.error.error), this.isModal)
          }
        }
      } else {
        this.setState({ processing: false })
        this.removeTagReadFromReader([this.lastSerial])
        this.lastSerial = null
        this.showToastErrorWithDatadog('Unknown error during encoding validation', __(T.error.error), this.isModal)
      }
    } catch (error) {
      this.setState({ processing: false })
      this.showToastErrorWithDatadog(
        (error as any).message ?? 'Unknown error during encoding validation',
        __(T.error.error),
        this.isModal
      )
    }
  }

  onTagReadDatadog = (tag: TmrTag) => {
    if (!this.datadogSession)
      this.initializeDataDogSession({
        ...this.state.formSchemaData,
        identifier: tag.epc ?? tag.uid ?? tag.barcode
      })
    if (this.datadogSession) {
      if (tag.uid) {
        if (!this.datadogSession.nfcRead) this.datadogSession.nfcRead = []
        this.datadogSession.nfcRead.push({
          offsetMs: this.getAntennaOffsetMs(true),
          nfcRead: tag.uid
        })
        this.trackDatadogAction('nfcRead')
      } else if (tag.epc) {
        if (!this.datadogSession.uhfRead) this.datadogSession.uhfRead = []
        this.datadogSession.uhfRead.push({
          offsetMs: this.getAntennaOffsetMs(true),
          uhfRead: tag.epc
        })
        this.trackDatadogAction('uhfRead')
      }
    }
  }

  /**
   * Runs NFC mutual-auth for a tag before it is ever used to search for an order (findOrderTags/findOrder).
   * Returns true when there's nothing to authenticate (not NFC, or auth disabled) or auth succeeded.
   */
  authenticateNfcTagForIdentifierFlow = async (tag: TmrTag, antennaId?: string): Promise<boolean> => {
    if (!tag.uid || !isNfcAuthEnabled(this.operation)) return true
    if (!antennaId) return false

    const throwawayValidation: EncodingValidationRequest = {
      operationId: this.operation.id,
      identifiers: [{ identifierType: 'NFC_TAG', code: tag.uid }]
    }
    this.isDecodeNfc = true
    this.setState({ processing: 'nfcDecryption' })
    try {
      const success = await readNfcAuthenticatedTag(
        throwawayValidation,
        tag,
        this.operation,
        antennaId,
        this.datadogSession
      )
      const { nfcError, nfcCollision } = getNfcTagReadErrors(throwawayValidation)
      return success && !nfcError && !nfcCollision
    } finally {
      this.isDecodeNfc = false
      this.setState({ processing: false })
    }
  }

  onTagReadIdentifier = async (tag: TmrTag, antennaId?: string) => {
    const identifierType = getTagType(tag)
    if (identifierType === 'NFC_TAG' && !(await this.authenticateNfcTagForIdentifierFlow(tag, antennaId))) {
      this.showToastErrorWithDatadog(__(T.error.tag_error_or_not_authentic), __(T.error.error), this.isModal)
      return
    }

    const { encodingValidation } = this.state
    const tmpTagsRead = this.state.tagsRead
    if (isSkipValidation(this.operation)) {
      tmpTagsRead.push(tag)
      this.findOrderTags.push({ code: tag.epc ?? tag.uid ?? tag.barcode, processed: false })
      EncodingExtensions.assignTagIdentifier(encodingValidation, tag)
      this.setState({ tagsRead: tmpTagsRead, encodingValidation: encodingValidation }, this.startTimerNoReads)
    } else {
      this.onTagReadDatadog(tag)
      if (!encodingValidation && identifierType === 'UHF_TAG') {
        tmpTagsRead.push(tag)
        this.findOrderTags.push({ code: tag.epc ?? tag.uid ?? tag.barcode, processed: false })
        this.setState({ tagsRead: tmpTagsRead }, this.startTimerTags)
      } else {
        tmpTagsRead.push(tag)
        this.findOrderTags.push({ code: tag.epc ?? tag.uid ?? tag.barcode, processed: false })
        if (encodingValidation) {
          EncodingExtensions.assignTagIdentifier(encodingValidation, tag)
          this.setState({ tagsRead: tmpTagsRead, encodingValidation: encodingValidation }, this.startTimerTags)
        } else {
          this.setState({ tagsRead: tmpTagsRead }, this.startTimerTags)
        }
      }
    }
  }

  findTagOrder = async (code: string) => {
    let order
    try {
      order = await Encodings.findOrder(this.operation.id, code, this.datadogSession?.runId)
    } catch {}
    return order
  }

  _doFindTagsOrder = async () => {
    const tags = this.findOrderTags.filter((f) => !f.processed)
    let order
    if (this.counterFindOrderTag < tags.length) {
      tags[this.counterFindOrderTag].processed = true
      order = await this.findTagOrder(tags[this.counterFindOrderTag].code)
      if (order) {
        this.findOrderInProgress = false
        this.counterFindOrderTag = tags.length
      } else {
        this.counterFindOrderTag++
        this._doFindTagsOrder()
      }
    } else {
      this.findOrderInProgress = false
      if (!order && this.findOrderTags.some((f) => !f.processed)) {
        await this._doSearchOrder()
      }
    }
    return order
  }

  _doSearchOrder = async () => {
    this.findOrderInProgress = true
    this.counterFindOrderTag = 0
    const order = await this._doFindTagsOrder()
    if (order) {
      this.setState({ orderFound: order }, this.doValidateIdentifiersTag)
    }
  }

  loadIdentifierFlowCounters = async (order: any, productCode?: string) => {
    let counters
    const productionOrderId = order?.productionOrder?.id

    if (order?.productionOrder?.code && productCode) {
      const equalCodes = `${order.productionOrder.code}${productCode}`
      try {
        const rowSearch = await EncodingProductionOrderRows.search<any>({ equalCodes, size: 1 })
        const productionOrderRowId = rowSearch?.content?.[0]?.id
        if (productionOrderRowId) {
          const rowCountersResponse = await Encodings.getProductionOrderRowCounter(productionOrderRowId)
          if (rowCountersResponse?.data) {
            counters = rowCountersResponse.data
          }
        }
      } catch {}
    }

    // Fallback for cases where row lookup is not available.
    if (!counters?.order && productionOrderId) {
      try {
        const countersResponse = await Encodings.getProductionOrderCounter(productionOrderId)
        counters = counters
          ? {
              ...countersResponse?.data,
              row: counters.row
            }
          : countersResponse?.data
      } catch {}
    }
    return counters
  }

  validateIdentifiersTag = async () => {
    const { orderFound } = this.state
    if (this.initialType === 'identifierOrderRow') {
      if (!orderFound && !this.findOrderInProgress) {
        await this._doSearchOrder()
      }
    } else {
      this.doValidateIdentifiersTag()
    }
  }

  doValidateIdentifiersTag = async () => {
    const { tagsRead } = this.state
    const tmpEncodingValidation: EncodingValidationRequest = {
      identifiers: [],
      operationId: this.operation.id,
      placeId: AppStore.defaultWorkstation?.placeId,
      workstationId: AppStore.defaultWorkstation?.id
    }
    // NFC auth (if any) already happened in onTagReadIdentifier before the tag reached tagsRead: just re-assign.
    for (const element of tagsRead) {
      EncodingExtensions.assignTagIdentifier(tmpEncodingValidation, element)
    }
    try {
      const res = await EncodingExtensions.validate(
        tmpEncodingValidation,
        false,
        false,
        getEnableUhfWrite(this.operation) !== 'no',
        isExpectOnlyMandatoryIdentifiers(this.operation),
        this.datadogSession?.runId
      )
      if (res.encodingValidationResponse.item?.product && res.encodingValidationResponse.item.product !== null) {
        this.clearTimerNoReads()
        await this.addIdentifierRole(res)
        await this.addIdentifierConfiguration(res)
        const { orderFound } = this.state

        const success = isSuccessEncodingCreate(res.encodingValidationResponse)
        if (success) {
          this.isEncoding = true
          this.encodingStart = new Date()

          const scanIdentifierConfig = getScanIdentifierConfig(this.operation)
          if (!scanIdentifierConfig.rfid) {
            await this.startAntenna()
          }
        } else {
          this.trackValidationErrors(res.encodingValidationResponse?.errors ?? [])
        }
        this.setState(
          {
            encodingValidationResponse: res.encodingValidationResponse,
            encodingValidation: res.encodingValidation,
            processing: success
          },
          async () => {
            if (this.isIdentifierFlow() && orderFound) {
              const productCode = res.encodingValidationResponse?.item?.product?.code
              const counters = await this.loadIdentifierFlowCounters(orderFound, productCode)
              this.setState({ counters })
            }
            if (success) {
              this.onTagReadValidateResponse()
            } else {
              this.startTimerNoReads()
            }
          }
        )
      } else {
        this.trackValidationErrors(res.encodingValidationResponse?.errors ?? [])
        this.setState({ processing: false })
        await this.stopAntenna()
        this.removeTagReadFromReader(tagsRead)
        let modalText = __(T.error.tags_errors)
        if (res?.encodingValidationResponse?.errors) {
          if (res.encodingValidationResponse.errors.some((e) => e.errorCode === 'MISSING_PRODUCT_INFO')) {
            modalText = __(T.error.only_virgin_tag)
          } else if (
            res.encodingValidationResponse.errors.some(
              (e) => e.errorCode === 'Tag Mismatch' || e.errorCode === 'TAG_MISMATCH'
            )
          ) {
            modalText = __(T.error.tags_mismatch_error)
          } else if (res.encodingValidationResponse.errors.some((e) => e.errorCode === 'NFC_TAG_NOT_EXPECTED')) {
            modalText = __(T.error.nfc_tag_not_expected)
          } else if (
            res.encodingValidationResponse.errors.some(
              (e) => e.errorCode === 'Missing Production Order Row Association'
            )
          ) {
            modalText = __(T.error.identifier_production_order_not_found)
          }
          this.showToastErrorWithDatadog(modalText, __(T.error.error), true, {
            onClose: () => {
              void this.closeVirginTagModal()
            },
            okText: 'OK'
          })
          this.feedbackSoundKo()
        }
        this.showToastErrorWithDatadog(modalText, __(T.error.error), true, {
          onClose: () => {
            void this.closeVirginTagModal()
          },
          okText: 'OK'
        })
        this.feedbackSoundKo()
      }
    } catch (error) {
      this.setState({ processing: false })
      this.removeTagReadFromReader(tagsRead)
      this.showToastErrorWithDatadog(
        (error as any).message ?? 'Unknown error during encoding validation',
        __(T.error.error),
        this.isModal
      )
    }
  }

  closeVirginTagModal = async () => {
    this.clearTimer()
    this.clearTimerTags()
    this.clearTimerNoReads()
    await this.startAntenna()
    this.findOrderTags = []
    this.setState({
      encodingValidation: undefined,
      encodingValidationResponse: undefined,
      counters: undefined,
      tagsRead: [],
      orderFound: undefined,
      associationStatus: 'TO_BE_READ',
      pin: '',
      forceAssociation: false,
      encodingCreateCounter: 0
    })
  }

  removeTagReadFromReader = (tags: TmrTag[]) => {
    const toRemove: string[] = []
    for (const element of tags) {
      toRemove.push(element.uid ?? element.epc)
    }
    RfidReader.removeTags(toRemove)
  }

  clearTimerNoReads = () => {
    if (this.timerNoRead) {
      clearTimeout(this.timerNoRead)
      this.timerNoRead = null
    }
  }

  call_encoding_with_confirmation = async (write: boolean) => {
    const { encodingValidationResponse } = this.state
    const errors = encodingValidationResponse?.errors ? encodingValidationResponse.errors : []
    // if only error with code MISSING_OPTIONAL_TAG and isExpectOnlyMandatoryIdentifiers do force create
    if (
      errors.length == 1 &&
      errors[0].errorCode === 'MISSING_OPTIONAL_TAG' &&
      isExpectOnlyMandatoryIdentifiers(this.operation)
    ) {
      this.call_force_encoding(write)
    } else {
      const ok = await askUserConfirmation(
        __(T.misc.attention),
        getWithUserConfirmationMessage(errors, encodingValidationResponse)
      )
      if (ok) {
        this.trackForcibleErrorsModalClick(errors)
        this.call_force_encoding(write)
      } else {
        this.clear()
        this.isEncoding = false
      }
    }
  }

  startTimerNoReads = async () => {
    const { encodingValidation } = this.state
    if (encodingValidation) {
      if (this.checkIfForceCreate('yes')) {
        this.call_force_encoding(false)
      } else if (this.checkIfForceCreate('withUserConfirmation')) {
        this.call_encoding_with_confirmation(false)
      } else {
        this.attachTimerNoReads()
      }
    }
  }

  addIdentifierRole = async (res) => {
    let idt
    if (res.encodingValidationResponse?.item?.identifiers) {
      for (const element of res.encodingValidationResponse.item.identifiers) {
        idt = res.encodingValidation.identifiers.find((e) => e.code === element.code)
        if (idt) {
          idt.role = element.role ?? ''
        }
      }
    }
  }

  getValidationResponseConfiguration = (res) => {
    let config
    if (res.encodingValidationResponse.configurationDetails)
      config = res.encodingValidationResponse.configurationDetails
    else if (res.encodingValidationResponse.configuration?.details)
      config = res.encodingValidationResponse.configuration.details
    if (!config) config = []
    return config
  }

  assignMissingRoles = (identifiers, config) => {
    for (const element of identifiers) {
      if (!element.role) {
        const cg = config.find((o) => o.identifierType === element.identifierType)
        if (cg) element.role = cg.role
      }
    }
  }

  buildMissingIdentifier = (element, res) => {
    let status = 'TO_BE_READ',
      code = '',
      read = false
    if (element.identifierType === 'SIMPLE_ITEM_IDENTIFIER') {
      const serial = res.encodingValidationResponse.item.identifiers.find(
        (e) => e.type === element.identifierType && e.role.toLowerCase() === element.role.toLowerCase()
      )
      if (serial && serial.code !== '') {
        status = 'CONFIRMED'
        code = serial.code
        read = true
      }
    }
    return {
      code,
      identifierType: element.identifierType,
      role: element.role,
      _status: status,
      _read: read,
      _error: undefined
    }
  }

  addIdentifierConfiguration = async (res) => {
    const config = this.getValidationResponseConfiguration(res)
    this.assignMissingRoles(res.encodingValidation.identifiers, config)

    const identifiers = res.encodingValidation.identifiers.filter((e) => e._status !== 'IGNORED')
    const expectOnlyMandatoryIdentifiers = isExpectOnlyMandatoryIdentifiers(this.operation)

    for (const element of config) {
      if (expectOnlyMandatoryIdentifiers && element.optional) continue
      const idt = identifiers.find(
        (e) => e.identifierType === element.identifierType && e.role.toLowerCase() === element.role.toLowerCase()
      )
      if (!idt) res.encodingValidation.identifiers.push(this.buildMissingIdentifier(element, res))
    }
  }

  clearTimerTags = () => {
    if (this.timerTags) {
      clearTimeout(this.timerTags)
      this.timerTags = null
    }
  }

  startTimerTags = () => {
    const timeoutValidateTags = this.operation.options.timeoutValidateTags ?? 1000
    if (this.state.encodingValidation) {
      // Phase 2: product found, new tag arrived — cancel the "waiting for more tags" timer
      this.clearTimerNoReads()
    }
    this.clearTimerTags()
    this.timerTags = setTimeout(async () => {
      if (this.state.encodingValidation) {
        //await this.stopAntenna()
        this.validateReadTags()
      } else {
        this.validateIdentifiersTag()
      }
    }, timeoutValidateTags)
  }

  validateReadTags = async () => {
    if (this.isEncoding) return
    this.isEncoding = true
    this.encodingStart = new Date()
    const tmpTagsRead = this.state.tagsRead
    const tmpEncodingValidation = this.state.encodingValidation
    if (tmpEncodingValidation) {
      // NFC auth (if any) already happened in onTagReadIdentifier before the tag reached tagsRead: just re-assign.
      for (const element of tmpTagsRead) {
        EncodingExtensions.assignTagIdentifier(tmpEncodingValidation, element)
      }
      try {
        // la sessione datadog viene inizializzata prima della validate per poterne inviare il runId nel payload
        if (!this.datadogSession)
          this.initializeDataDogSession({
            ...this.state.formSchemaData,
            identifier: tmpTagsRead[0].epc ?? tmpTagsRead[0].uid ?? tmpTagsRead[0].barcode
          })
        const res = await EncodingExtensions.validate(
          tmpEncodingValidation,
          true,
          false,
          getEnableUhfWrite(this.operation) !== 'no',
          isExpectOnlyMandatoryIdentifiers(this.operation),
          this.datadogSession?.runId
        )
        this.setState(
          {
            encodingValidationResponse: res.encodingValidationResponse,
            encodingValidation: tmpEncodingValidation
          },
          this.onTagReadValidateResponse
        )
      } catch (error) {
        this.isEncoding = false
        this.encodingError(error, 'Unknown error during encoding validation')
        this.onClear()
      }
    } else {
      this.isEncoding = false
      this.onClear()
    }
  }

  checkIfForceCreate = (value) => {
    const { encodingValidationResponse } = this.state
    let canForce = false
    if (getCanForceAssocation(this.operation) === value) {
      const notForcible = encodingValidationResponse?.errors.find((e) => !e.forcible)
      //if only errors with forcible = true then force create
      canForce = !notForcible
    }
    return canForce
  }

  call_force_encoding = (write: boolean) => {
    if (!write) {
      this.isEncoding = true
      this.encodingStart = new Date()
    }
    this.setState(
      (prevState) => ({
        forceAssociation: true,
        pin: '',
        encodingCreateCounter: prevState.encodingCreateCounter + 1
      }),
      write ? this.checkWriteTag : this.create
    )
  }

  additionalOnTagReadCheck = (_tag: TmrTag) => {
    return true
  }

  getNextTagType = () => {
    const { currentTagType } = this.state

    const currentTagTypeIndex = this.tagTypesToCheck.indexOf(currentTagType)
    let nextTagType: OperationEncodingTagType
    if (currentTagType !== 'ALL' && currentTagTypeIndex + 1 < this.tagTypesToCheck.length) {
      nextTagType = this.tagTypesToCheck[currentTagTypeIndex + 1]
    }
    return nextTagType
  }

  isNfcAuthNeededForTag = (tag: TmrTag, encodingValidationResponse?: EncodingValidationResponse) => {
    return (
      !!tag.uid &&
      isNfcAuthEnabled(this.operation) &&
      !!encodingValidationResponse?.configuration?.details.some((conf) => conf.identifierType === 'NFC_TAG')
    )
  }

  /** Returns true if the read should be aborted (NFC auth error or collision). */
  handleNfcAuthFailure = (encodingValidation: EncodingValidationRequest): boolean => {
    const { nfcError, nfcCollision } = getNfcTagReadErrors(encodingValidation)
    if (!nfcError && !nfcCollision) {
      this.setState({ processing: false })
      return false
    }
    this.setState({ processing: false, associationStatus: 'ERROR' }, async () => {
      this.isEncoding = false
      await this.stopAntenna()
      this.clearTimer()
      this.clearTimerReader()
    })
    if (nfcCollision && this.datadogSession) {
      this.datadogSession.errors?.push({ error: 'nfcCollision' })
      datadogRum.addAction('err_nfcCollision', this.datadogSession)
    }
    return true
  }

  trackTagReadEvent = (tag: TmrTag) => {
    if (!this.datadogSession) return
    this.cleanDataDogSessionDeviceManager()
    if (tag.uid) {
      if (!this.datadogSession.nfcRead) this.datadogSession.nfcRead = []
      this.datadogSession.nfcRead.push({
        offsetMs: this.getAntennaOffsetMs(true),
        offsetFromAntennaStart: this.getAntennaOffsetMs(),
        nfcRead: tag.uid
      })
      this.trackDatadogAction('nfcRead')
    } else if (tag.epc) {
      if (!this.datadogSession.uhfRead) this.datadogSession.uhfRead = []
      this.datadogSession.uhfRead.push({
        offsetMs: this.getAntennaOffsetMs(true),
        offsetFromAntennaStart: this.getAntennaOffsetMs(),
        uhfRead: tag.epc
      })
      this.trackDatadogAction('uhfRead')
    }
  }

  onTagRead = async (tag: TmrTag, antennaId?: string) => {
    //added a sleep to destroy correctly the timers or wait isEncoding flag
    await sleep(Math.random() * 500)

    const { encodingValidation, encodingValidationResponse } = this.state
    if (!encodingValidation?.identifiers) return
    if (this.isEncoding) {
      this.removeTagReadFromReader([tag])
      return
    }
    if (this.isDecodeNfc) {
      this.removeTagReadFromReader([tag])
      return
    }
    if (!this.additionalOnTagReadCheck(tag)) return
    // if time to validate is 0 then set isEncoding to true immediately for not have validated in parallel
    const timeoutValidateTags = getTimeoutValidateTags(this.operation, this.getIgnoreUnknownTags())
    if (timeoutValidateTags !== undefined && timeoutValidateTags === 0) {
      this.isEncoding = true
    }
    try {
      const nextTagType = this.getNextTagType()
      if (nextTagType || this.operation.autostopAntennaTimeout <= 0) {
        RfidReader.setAutomaticStop(false)
        RfidReader.stopTimer()
        this.clearTimerReader()
      }
      this.clearTimer()

      const nfcNeedsAuthentication = this.isNfcAuthNeededForTag(tag, encodingValidationResponse)
      if (nfcNeedsAuthentication) {
        this.isDecodeNfc = true
        this.setState({ processing: 'nfcDecryption' })
      }

      await EncodingExtensions.onTagRead(encodingValidation, tag, this.operation, antennaId, this.datadogSession)

      if (nfcNeedsAuthentication && this.handleNfcAuthFailure(encodingValidation)) {
        return
      }

      if (nextTagType) {
        await RfidReader.stop({ manual: true, customCommandsOperationType: this.getAntennaCustomCommands() })
      } else if (
        tag.barcode &&
        !RfidReader.isReading() &&
        this.isRequestedSimpleItemIdentifier() &&
        encodingStartAntennaAfterSimpleItemIdentifier()
      ) {
        await this.startReader()
        return
      }
      this.setState(
        (prevState) => ({
          encodingValidation,
          currentTagType: nextTagType || prevState.currentTagType,
          startedTagType: 'ALL'
        }),
        async () => {
          this.trackTagReadEvent(tag)
          if (nextTagType) {
            await sleep(100)
            await this.startAntenna(nextTagType)
          }
          this.onTagReadTimer()
        }
      )
    } catch {
    } finally {
      this.isDecodeNfc = false
    }
  }

  onTagReadTimer = () => {
    const timeoutValidateTags = getTimeoutValidateTags(this.operation, this.getIgnoreUnknownTags())
    if (timeoutValidateTags !== undefined && timeoutValidateTags > 0) {
      // Ogni tag letto deve resettare il debounce: cancella l'eventuale timer
      // già in sospeso prima di crearne uno nuovo. Senza questo, con letture
      // concorrenti (es. emulazione) si accumulano più timer vivi e partono
      // più validate. La clearTimer() in onTagRead non basta perché viene
      // eseguita prima che il timer precedente venga effettivamente settato.
      this.clearTimer()
      this.timer = setTimeout(() => {
        this.onTagReadValidate()
      }, timeoutValidateTags)
    } else {
      this.onTagReadValidate()
    }
  }

  onTagReadValidate = async () => {
    // Rete di sicurezza: se una validate è già in corso non ne avviamo un'altra
    // in parallelo (un secondo timer sfuggito non deve produrre una doppia validate).
    if (this.isEncoding) return
    const { encodingValidation } = this.state
    try {
      this.isEncoding = true
      this.encodingStart = new Date()
      const res = await EncodingExtensions.validate(
        encodingValidation,
        true,
        false,
        getEnableUhfWrite(this.operation) !== 'no',
        isExpectOnlyMandatoryIdentifiers(this.operation),
        this.datadogSession?.runId
      )
      this.setState(
        {
          encodingValidationResponse: res.encodingValidationResponse,
          encodingValidation: res.encodingValidation
        },
        this.onTagReadValidateResponse
      )
    } catch (error) {
      this.clearReads(false, true)
      this.encodingError(error, 'Unknown error during encoding validation')
    } finally {
      this.isEncoding = false
    }
  }

  feedbackSoundOk = () => {
    if (this.state.enableOkSounds) Sounds.success()
  }

  feedbackSoundKo = () => {
    if (this.state.enableKoSounds) Sounds.error()
  }

  encodingFail = async (options?: { message?: string; errors?: any[]; skipLog?: boolean }) => {
    const { encodingValidation } = this.state
    encodingValidation.identifiers.forEach((idf) => {
      if (['PROCESSING', 'TO_BE_READ'].includes(idf._status)) {
        idf._status = 'SKIPPED'
      }
    })
    this.setState({ associationStatus: 'ERROR', processing: false })
    await this.stopAntenna()
    this.isEncoding = false
    this.isDecodeNfc = false
    if (!options?.skipLog) {
      void this.trackEncodingError({ actionname: 'encodingFail', message: options?.message, errors: options?.errors })
    }
  }

  encodingError = (error?: any, text?: string) => {
    this.feedbackSoundKo()
    const message = error?.message ? error.message : (text ?? 'Unknown error during encoding validation')
    this.showToastErrorWithDatadog(message, __(T.error.error), this.isModal, {
      errors: [error ?? { message }]
    })
  }

  onValidationOperationNone = async () => {
    await this.stopAntenna()
    this.setState({ processing: false, associationStatus: 'SKIPPED' })
    if (this.operation.options.onItemAlreadyEncodedFeedback === 'confirmModal') {
      AppStore.showFeedbackModal(
        __(T.misc.attention),
        __(T.messages.encoding_success_none),
        () => {},
        __(T.misc.confirm)
      )
    } else {
      showToast({ status: 'success', description: __(T.messages.encoding_success_none) })
    }
    this.feedbackSoundOk()
    setTimeout(async () => {
      this.resetAfterCreate()
    }, this.operation.options.resetReadingsAfterOperation || 300)
  }

  sendMixpanelEventData = async (event) => {
    const { encodingValidation, encodingValidationResponse, forceAssociation } = this.state
    const endDate = new Date()
    const seconds = this.encodingStart ? (endDate.getTime() - this.encodingStart.getTime()) / 1000 : 0
    const mixpanelData = EncodingExtensions.getMixPanelData(
      this.operation,
      encodingValidation,
      encodingValidationResponse,
      seconds,
      forceAssociation ? 'yes' : 'no'
    )
    AppStore.sendMixPanelEvent(event, mixpanelData)
  }

  checkValidateAfterRemoveUnknownTags = () => {
    const { encodingValidation } = this.state
    if (encodingValidation) {
      const findCode = encodingValidation.identifiers.find((idt) => idt.code && idt.code !== '')
      if (findCode) {
        this.onTagReadValidate()
      } else {
        this.isEncoding = false
      }
    } else {
      this.isEncoding = false
    }
  }

  // callback response validate on flow product and order
  onTagReadValidateResponse = async () => {
    const { encodingValidationResponse, encodingValidation, encodingCreateCounter } = this.state
    if (encodingValidationResponse) {
      this.trackValidationErrors(encodingValidationResponse.errors ?? [])
      if (
        encodingValidationResponse.errors?.length > 0 &&
        encodingValidationResponse.errors.some(
          (e) => !['MISSING_OPTIONAL_TAG', 'MISSING_MANDATORY_TAG'].includes(e.errorCode)
        )
      ) {
        this.feedbackSoundKo()
      }
      // remove ignored tags if request from operation option
      if (
        this.getIgnoreUnknownTags() &&
        encodingValidation &&
        encodingValidationResponse.errors.some((el) => el.errorCode === 'UNKNOWN_TAG')
      ) {
        let resetIdentifiers = false
        const identifiers = encodingValidation.identifiers ?? []
        const newIdentifiers: ConfigurationDetail[] = []
        let find
        identifiers.forEach((idt) => {
          const checkUnknown = encodingValidationResponse.errors.find(
            (el) => el.errorCode === 'UNKNOWN_TAG' && el.ref === idt.code
          )
          if (!checkUnknown) {
            if (idt._added) {
              // to management case with code not associated at same identifierType
              find = identifiers.find((f) => !f._added && idt.identifierType === f.identifierType)
              if (find) {
                newIdentifiers.push({
                  id: find.id ?? '',
                  creationDate: find.creationDate ?? '',
                  identifierType: find.identifierType,
                  role: find.role ?? '',
                  optional: find.optional ?? false,
                  regexFilter: find.regexFilter ?? '',
                  code: idt.code,
                  _status: idt.code && idt.code !== '' ? 'CONFIRMED' : 'PROCESSING'
                })

                resetIdentifiers = true
              } else {
                resetIdentifiers = true
                newIdentifiers.push(idt)
              }
            } else {
              resetIdentifiers = true
              newIdentifiers.push(idt)
            }
          } else if (!idt._added) {
            resetIdentifiers = true
            newIdentifiers.push({
              id: idt.id ?? '',
              creationDate: idt.creationDate ?? '',
              identifierType: idt.identifierType,
              role: idt.role ?? '',
              optional: idt.optional ?? false,
              regexFilter: idt.regexFilter ?? ''
            })
          }
        })

        if (resetIdentifiers) {
          const finalIdentifiers: ConfigurationDetail[] = newIdentifiers.filter((f) => f.code && f.code !== '')
          newIdentifiers.find((idt) => {
            find = finalIdentifiers.find((f) => f.role === idt.role && f.identifierType === idt.identifierType)
            if (!find) {
              finalIdentifiers.push(idt)
            }
          })
          this.setState(
            { encodingValidation: { ...encodingValidation, identifiers: finalIdentifiers } },
            this.checkValidateAfterRemoveUnknownTags
          )
          return
        }
      }

      if (encodingValidationResponse.errors && encodingValidationResponse.errors.length > 0) {
        const quantityExceededError = encodingValidationResponse.errors.find(
          (el) => el.errorCode === 'ENCODABLE_QUANTITY_EXCEEDED'
        )
        if (quantityExceededError) {
          this.showToastErrorWithDatadog(
            __(T.messages.exceeded_quantity_message),
            __(T.error.exceeded_quantity),
            this.isModal
          )
          this.encodingFail({ skipLog: true })
          return
        }

        // Fail encoding based on error codes
        if (
          ['EXTRA_TAG', 'UNEXPECTED_TAG', 'TOOL_DETECTED'].some((errorCode) =>
            encodingValidationResponse.errors.some((el) => el.errorCode === errorCode)
          )
        ) {
          this.encodingFail()
          return
        }

        if (encodingValidationResponse.errors.some((el) => el.errorCode === 'OTHER_PRODUCTION_ORDER')) {
          showToastError(__(T.error.other_production_order), __(T.error.error), this.isModal)
          this.encodingFail()
          return
        }

        // if there are MISSING_MANDATORY_TAG, MISSING_MANDATORY_TAG_DUAL errors we are waiting for the other requested tags
        const checkMissing = encodingValidationResponse.errors.find((el) =>
          ['MISSING_MANDATORY_TAG', 'MISSING_MANDATORY_TAG_DUAL'].includes(el.errorCode)
        )
        if (checkMissing) {
          this.isEncoding = false
          return
        }
      }

      if (
        this.datadogSession &&
        encodingValidationResponse.operationToPerform !== 'FAIL' &&
        !encodingValidationResponse.operationToPerform.startsWith('FAIL_')
      ) {
        this.datadogSession.allTagsValidated = {
          offsetMs: this.getAntennaOffsetMs(true),
          operationToPerform: encodingValidationResponse.operationToPerform
        }
        if (!this.isIdentifierFlow()) {
          this.datadogSession.allTagsValidated.offsetFromAntennaStart = this.getAntennaOffsetMs()
        }
        this.trackDatadogAction('allIdentifiersValidated')
      }

      if (
        encodingValidationResponse.operationToPerform === 'NONE' &&
        this.operation.options.forceTagRewrite !== 'true'
      ) {
        await this.sendMixpanelEventData('Encoding: Item Previously Associated')
        await this.onValidationOperationNone()
      } else if (
        encodingValidationResponse.operationToPerform === 'NONE' &&
        this.operation.options.forceTagRewrite === 'true'
      ) {
        this.forceTagRewrite()
      } else if (
        ['UPDATE_ONLY_PRODUCT', 'UPDATE_WAM', 'UPDATE_PRODUCTION_ORDER_ROW'].includes(
          encodingValidationResponse.operationToPerform
        )
      ) {
        Sounds.confirmation()

        const readTag = encodingValidation?.identifiers
          ? encodingValidation?.identifiers.length === 1
            ? encodingValidation?.identifiers[0]
            : encodingValidation?.identifiers?.length > 1
              ? encodingValidation?.identifiers.find((id) => id._status === 'CONFIRMED')
              : undefined
          : undefined
        const responseItemIds = encodingValidationResponse?.item?.identifiers
        const responseItemId = responseItemIds
          ? responseItemIds.length === 1
            ? responseItemIds[0]
            : responseItemIds?.length > 1
              ? responseItemIds.find((id) => id.code === readTag.code)
              : undefined
          : undefined

        const modalMessage: Partial<Record<EncodingOperationToPerform, string>> = {
          UPDATE_ONLY_PRODUCT: __(T.misc.update_only_product),
          UPDATE_WAM: __(T.misc.update_only_product),
          UPDATE_PRODUCTION_ORDER_ROW: __(T.misc.update_production_order_row)
        }

        const ok = await askUserConfirmation(
          __(T.misc.attention),
          modalMessage[encodingValidationResponse.operationToPerform] ??
            __(T.messages.are_you_sure_you_want_to_confirm),
          undefined,
          undefined,
          readTag
            ? {
                identifierCode: readTag.code,
                productionOrderRowId: responseItemId?.productionOrderRowId
              }
            : undefined
        )
        if (ok) {
          this.setState({ encodingCreateCounter: encodingCreateCounter + 1 }, this.checkWriteTag)
        } else {
          if (getAskUserInputEveryTime(this.operation)) {
            await this.stopAntenna()
          }
          this.clear()
          this.isEncoding = false
          await this.sendMixpanelEventData('Encoding: Update product not confirmed')
        }
      } else if (
        encodingValidationResponse.operationToPerform === 'FAIL' ||
        encodingValidationResponse.operationToPerform.startsWith('FAIL_')
      ) {
        await this.sendMixpanelEventData('Encoding: error')
        this.encodingFail({ message: encodingValidationResponse.operationToPerform })
      } else if (encodingValidationResponse.operationToPerform.startsWith('FORCE_')) {
        this.clearTimerReader()
        this.checkEncodingForceOption()
      } else {
        this.setState({ encodingCreateCounter: encodingCreateCounter + 1 }, this.checkWriteTag)
      }
    } else {
      await this.sendMixpanelEventData('Encoding: error')
      this.encodingFail({ message: 'encodingValidationResponse_missing' })
    }
  }

  forceTagRewrite = async () => {
    try {
      const { encodingValidation, formSchemaData, numWriteAttempt, encodingValidationResponse } = this.state
      const identifiers = encodingValidation?.identifiers ?? []
      if (!encodingValidation || identifiers.length === 0) throw new Error(__(T.error.invalid_tag_read))
      if (identifiers.filter((idt) => idt.identifierType === 'UHF_TAG').length > 1)
        throw new Error(__(T.error.read_one_tag_time))
      await this.stopAntenna()

      const res = await EncodingExtensions.computeTags(
        this.operation,
        identifiers.filter((el) => el.code && el.code !== ''),
        AppStore.defaultWorkstation?.id ?? '',
        '',
        encodingComputeTagsEntityCode(encodingValidationResponse, this.initialType, formSchemaData), //entity code
        undefined,
        undefined,
        this.datadogSession?.runId
      )
      const tmpEncodingValidation = encodingValidation
      const identifierTag = tmpEncodingValidation.identifiers.find((idt) => idt.identifierType === 'UHF_TAG')
      if (!identifierTag?.code) throw new Error(__(T.error.uhf_tag_not_found))

      const desiderCode = getDesiredCode(res)
      if (desiderCode === '') {
        throw new Error(__(T.error.compute_tags_failed))
      } else {
        identifierTag.desiredCode = desiderCode
        this.setState({ encodingValidation: tmpEncodingValidation }, async () => {
          const validateRes = await EncodingExtensions.validate(
            tmpEncodingValidation,
            true,
            true,
            getEnableUhfWrite(this.operation) !== 'no',
            isExpectOnlyMandatoryIdentifiers(this.operation),
            this.datadogSession?.runId
          )
          if (validateRes.encodingValidationResponse.operationToPerform !== 'REWRITE_CONFIGURATION')
            throw new Error(__(T.error.validation_failed_rewrite))
          this.setState(
            {
              targetTag: identifierTag.code ?? '',
              targetTid: identifierTag.tid ?? '',
              associationStatus: 'WRITING',
              encodingValidationResponse: validateRes.encodingValidationResponse,
              tagToWrite: res[0].desiredCode,
              numWriteAttempt: numWriteAttempt + 1,
              writeRetryCount: 0
            },
            this.writeTag
          )
        })
      }
    } catch (error) {
      this.encodingError(error)
      this.clear()
    }
  }

  attachTimerNoReads = () => {
    const timer = this.operation.options.timeoutTagsNoReads ?? 0
    if (timer > 0) {
      this.clearTimerNoReads()
      this.timerNoRead = setTimeout(async () => {
        if (isSkipValidation(this.operation)) {
          this.setState(
            (prevState) => ({
              encodingCreateCounter: prevState.encodingCreateCounter + 1
            }),
            this.create
          )
        } else {
          this.feedbackSoundKo()
          void this.trackEncodingError({ actionname: 'timeoutTagsNoReads' })
          this.setState({ associationStatus: 'ERROR' })
        }
      }, timer)
    }
  }

  checkEncodingForceOption = async () => {
    const { encodingValidationResponse } = this.state
    if (encodingValidationResponse) {
      const encodingWrite = !this.isIdentifierFlow()
      if (this.checkIfForceCreate('yes')) {
        this.call_force_encoding(encodingWrite)
      } else if (this.checkIfForceCreate('withUserConfirmation')) {
        this.call_encoding_with_confirmation(encodingWrite)
      } else if (encodingWrite) {
        this.checkAttachAutomaticReaderStop(encodingValidationResponse)
      } else {
        this.attachTimerNoReads()
      }
    } else {
      this.feedbackSoundKo()
      this.setState({ processing: false })
      this.isEncoding = false
    }
  }

  checkAttachAutomaticReaderStop = async (encodingValidationResponse) => {
    this.isEncoding = false
    if (this.operation.autostopAntennaTimeout > 0) {
      const blockedErrors = encodingValidationResponse
        ? encodingValidationResponse.errors.filter((e) => BLOCKED_ERRORS.includes(e.errorCode))
        : []
      if (blockedErrors.length === 0) {
        this.attachAutomaticReaderStop()
      } else {
        this.feedbackSoundKo()
        void this.trackEncodingError({ actionname: 'blockedErrors', errors: blockedErrors })
        this.encodingFail()
      }
    }
  }

  getIdentifierWriteTag = () => {
    const { encodingValidation, encodingValidationResponse } = this.state
    //si prendono tag UHF
    const identifiers = encodingValidation?.identifiers || []
    const uhfIdentifiers: any[] = []
    let r = 0
    for (r = 0; r < identifiers.length; r++) {
      if (identifiers[r].identifierType === 'UHF_TAG') {
        uhfIdentifiers.push(identifiers[r])
      }
    }

    let identifierTag: any = undefined
    if (uhfIdentifiers.length === 1) {
      identifierTag = uhfIdentifiers[0]
    } else {
      const uhfRoleToWrite = getUhfTagToWriteRole(this.operation, encodingValidationResponse.configuration?.code)
      if (uhfRoleToWrite) {
        identifierTag = uhfIdentifiers.find((id) => id.role === uhfRoleToWrite)
      }
    }
    return identifierTag
  }

  checkWriteTag = async () => {
    if (getEnableUhfWrite(this.operation) === 'no') {
      this.create()
    } else {
      await this.stopAntenna()

      const identifierTag = this.getIdentifierWriteTag()
      if (identifierTag) {
        if (identifierTag.desiredCode) {
          this.create()
        } else {
          this.setState(
            { targetTag: identifierTag.code, targetTid: identifierTag.tid, associationStatus: 'WRITING' },
            this.computeTags
          )
        }
      } else {
        this.clear()
        this.showToastErrorWithDatadog('UHF tag to write not found', __(T.error.error), this.isModal)
      }
    }
  }

  computeTags = async () => {
    const { formSchemaData, numWriteAttempt, encodingValidationResponse, encodingValidation, targetTag } = this.state
    try {
      const res = await EncodingExtensions.computeTags(
        this.operation,
        (encodingValidation?.identifiers ?? []).filter((el) => (targetTag ? el.code === targetTag : el.code)),
        AppStore.defaultWorkstation?.id ?? '',
        '',
        encodingComputeTagsEntityCode(encodingValidationResponse, this.initialType, formSchemaData), //entity code
        undefined,
        undefined,
        this.datadogSession?.runId
      )
      const desiderCode = getDesiredCode(res)
      if (desiderCode === '') {
        // errore
        this.showToastErrorWithDatadog(__(T.error.compute_tags_failed), __(T.error.error), this.isModal)
        this.encodingFail({ skipLog: true })
      } else {
        //si scrive il nuovo tag con la write del reader
        this.setState(
          { tagToWrite: desiderCode, numWriteAttempt: numWriteAttempt + 1, writeRetryCount: 0 },
          this.writeTag
        )
      }
    } catch (error) {
      this.showToastErrorWithDatadog(error, __(T.error.error), this.isModal)
      this.encodingFail({ skipLog: true })
    }
  }

  /* verifica l'effettiva scrittura rileggendo il tag, con un secondo tentativo dopo 200 millisecondi */
  checkWrittenTag = async (writtenEpc: string) => {
    this.cleanDataDogSessionDeviceManager()

    let check = await this.executeReadEpcWithMetrics(writtenEpc)
    if (check.ok) return true

    // wait 200 milliseconds
    await sleep(200)

    // second check to verify writing
    check = await this.executeReadEpcWithMetrics(writtenEpc)
    return check.ok
  }

  writeTag = async () => {
    const { targetTag, targetTid, tagToWrite, writeRetryCount } = this.state

    if (!this.writeTagStartTime) {
      this.writeTagStartTime = new Date()
    }

    if (AppStore.getEmulation()) {
      this.writeSuccess()
      return
    }

    try {
      // define use tid to write
      let useTidToWrite = !!(getUseTidTargetDuringWrite(this.operation) && targetTid && targetTid !== '')

      // get antenna uhf
      const antennaUHF = RfidReader.getAntenna()

      // deactivate write with tid for device caen-slate
      if (antennaUHF?.reader?.deviceType === 'caen-slate') useTidToWrite = false

      const writePasswords: string[] = this.getTagWritePassword()

      let write: any = await RfidReader.writeEpc(
        useTidToWrite ? targetTid : targetTag,
        tagToWrite,
        writePasswords,
        useTidToWrite,
        this.datadogSession,
        { action: 'writeEpcDeviceManager', writeRetryCount: writeRetryCount, writeTagStartTime: this.writeTagStartTime }
      )
      if (!write.ok && getLockTagOnWriteFailure(this.operation)) {
        const lock: any = await RfidReader.lockTag(
          useTidToWrite ? targetTid : targetTag,
          tagToWrite,
          writePasswords,
          useTidToWrite,
          this.datadogSession
        )
        if (lock.ok) {
          write = await RfidReader.writeEpc(
            useTidToWrite ? targetTid : targetTag,
            tagToWrite,
            writePasswords,
            useTidToWrite,
            this.datadogSession,
            {
              action: 'writeEpcDeviceManager',
              writeRetryCount: writeRetryCount,
              writeTagStartTime: this.writeTagStartTime
            }
          )
        }
      }
      if (write.ok) {
        // se opzione abilitata si prova a leggere per verificare l'effettiva scrittura
        if (getCheckTagAfterWrite(this.operation) && !(await this.checkWrittenTag(tagToWrite))) {
          this.setState({ numWriteAttempt: MAX_WRITE_ATTEMPT }, this.writeTagError)
          return
        }
        this.writeSuccess()
      } else {
        this.writeTagError()
      }
    } catch {
      this.writeTagError()
    }
  }

  writeSuccess = () => {
    const { targetTag, tagToWrite, encodingValidation } = this.state
    this.totalWriteTagTime = this.writeTagStartTime ? Date.now() - this.writeTagStartTime.getTime() : 0
    this.writeTagStartTime = undefined
    if (encodingValidation) {
      //update encodingValidation by adding attribute to the identifier
      const identifiers = encodingValidation?.identifiers || []
      const index = identifiers.findIndex((el) => el.code === targetTag)
      if (index >= 0) {
        encodingValidation.identifiers[index].desiredCode = tagToWrite
      }
      this.setState({ encodingValidation: encodingValidation, associationStatus: 'TO_BE_READ' }, this.create)
    } else {
      this.encodingError()
    }
  }

  writeTagError = async () => {
    const { numWriteAttempt } = this.state
    if (numWriteAttempt === MAX_WRITE_ATTEMPT) {
      const ok = await askUserConfirmation(__(T.misc.write_tag), __(T.misc.retry_write_tag))
      if (ok) {
        this.setState(
          (prevState) => ({
            numWriteAttempt: 1,
            writeRetryCount: prevState.writeRetryCount + 1
          }),
          this.writeTag
        )
      } else {
        this.writeTagStartTime = undefined
        this.showToastErrorWithDatadog(__(T.error.write_tag), __(T.error.error), this.isModal)
        this.clear()
      }
    } else {
      this.setState(
        (prevState) => ({
          numWriteAttempt: numWriteAttempt + 1,
          writeRetryCount: prevState.writeRetryCount + 1
        }),
        this.writeTag
      )
    }
  }

  get_payload_create = () => {
    const { encodingValidation, formSchemaData } = this.state
    // if initialType as formSchema use getDataFromSchema for use confirmValuePath attribute for autocomplete fields
    const payload = encodingValidation
    if (this.initialType === 'formSchema') {
      const schema = EncodingExtensions.formSchema(this.operation)
      const formatFormSchemaData = getDataFromSchema(formSchemaData, schema)
      schema.forEach((s) => {
        if (!s.isProductCode && !s.initialType) {
          const val = _get(formatFormSchemaData, s.name)
          if (val) {
            _set(payload, s.name, val)
          }
        }
      })
    }
    return payload
  }

  send_datadag_success_create = (start: number) => {
    const { writeRetryCount, forceAssociation } = this.state
    if (this.datadogSession) {
      this.cleanDataDogSessionDeviceManager()
      this.datadogSession.encodingRequested = {
        apiDuration: Date.now() - start,
        offsetMs: this.getAntennaOffsetMs(true)
      }
      if (getEnableUhfWrite(this.operation) !== 'no' && this.totalWriteTagTime !== undefined) {
        this.datadogSession.writeTag = {
          totalWriteDuration: this.totalWriteTagTime,
          writeRetryCount: writeRetryCount
        }
      }
      this.trackDatadogAction(forceAssociation ? 'forceCreateSuccess' : 'encodingSuccess')
    }
  }

  create = async () => {
    const { forceAssociation, pin, encodingValidation, encodingValidationResponse } = this.state
    if (encodingValidation) {
      await this.stopAntenna()
      const skipValidation = isSkipValidation(this.operation)
      try {
        const payload = this.get_payload_create()
        const requestStart: number = Date.now()

        let res
        if (forceAssociation) res = await EncodingExtensions.force_create(payload, pin, this.datadogSession?.runId)
        else res = await EncodingExtensions.create(payload, this.datadogSession?.runId)
        if (!res) throw new Error(__(T.error.item_creation_error))

        const success = isSuccessEncodingCreate(res)
        const updatedCounters = this.getUpdatedCounters(success)
        if (skipValidation) {
          await EncodingExtensions.set_identifiers_status(
            encodingValidation,
            res,
            getEnableUhfWrite(this.operation) !== 'no',
            isExpectOnlyMandatoryIdentifiers(this.operation)
          )
          this.setState({
            encodingCreateResponse: res,
            encodingValidationResponse: res,
            encodingValidation: encodingValidation,
            processing: false,
            associationStatus: getEncodingAssociationStatus(res, encodingValidationResponse),
            counters: updatedCounters
          })
        } else {
          this.setState({
            encodingCreateResponse: res,
            processing: false,
            associationStatus: getEncodingAssociationStatus(res, encodingValidationResponse),
            counters: updatedCounters
          })
        }
        this.create_response(success, requestStart)
      } catch (error) {
        this.create_error_catch(error)
      }
    } else {
      this.setState({ processing: false })
      this.encodingError(undefined, __(T.error.item_creation_error))
    }
  }

  create_response = async (success: boolean, start: number) => {
    const { forceAssociation } = this.state
    if (success) {
      this.send_datadag_success_create(start)
      await this.sendMixpanelEventData('Encoding: Item Associated')
      await AppStore.increaseDailyEncodedItems(this.operation.code)
      showToast({ status: 'success', description: __(T.messages.encoding_success) })
      this.feedbackSoundOk()

      //if success reset operation after time in milliseconds
      setTimeout(
        async () => {
          this.resetAfterCreate()
        },
        this.operation.options.resetReadingsAfterOperation || (isSkipValidation(this.operation) ? 4000 : 300)
      )
    } else {
      await this.sendMixpanelEventData('Encoding: error')
      this.encodingError(undefined, __(T.error.item_creation_error))

      this.isEncoding = false
      if (!forceAssociation) {
        await this.startAntenna()
      }
      this.setState({ processing: false })
    }
  }

  create_error_catch = async (error) => {
    const { forceAssociation, tagsRead } = this.state
    if (this.isIdentifierFlow()) {
      this.removeTagReadFromReader(tagsRead)
      this.closeVirginTagModal()
    } else {
      this.setState({ processing: false })
      this.attachAutomaticReaderStop()
    }
    if (!forceAssociation) {
      await this.startAntenna()
    }
    this.isEncoding = false
    this.encodingError(error, 'Unknown error during encoding validation')
  }

  resetAfterCreate = async () => {
    if (this.isIdentifierFlow()) {
      this.onClear(true)
    } else {
      let nosk = false
      if (isPreEncodingWorkOrder(this.operation) && this.operation.options?.workOrderRowSelectionMode === 'sizeGrid') {
        const { counters } = this.state
        if (
          isOrderCompleted(
            counters,
            isCheckEncodedQuantity(this.operation),
            isCanEncodeWithOverQuantity(this.operation)
          )
        ) {
          showToast({ status: 'success', description: __(T.error.work_order_completed) })
        } else {
          nosk = true
        }
      }
      this.clearReads(true, nosk)
    }
  }

  clearTimer = () => {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
  }

  isCanForceAssociation = () => {
    const { encodingValidationResponse } = this.state
    if (encodingValidationResponse) {
      //se tutti errori forzabili
      const notForcible = encodingValidationResponse.errors.find((e) => !e.forcible)
      return !notForcible
    }
    return false
  }

  checkForceAssociation = () => {
    const { encodingCreateCounter } = this.state
    const canForceAssociation = getCanForceAssocation(this.operation)
    if (canForceAssociation === 'no') {
      this.clear()
    } else if (this.isCanForceAssociation()) {
      if (canForceAssociation === 'withPin') {
        this.managerPinModal()
      } else if (canForceAssociation === 'afterRetry') {
        if (encodingCreateCounter === 1) {
          this.setState({ encodingCreateCounter: encodingCreateCounter + 1 }, this.create)
        } else if (encodingCreateCounter === 2) {
          this.setState(
            { forceAssociation: true, pin: '', encodingCreateCounter: encodingCreateCounter + 1 },
            this.create
          )
        } else {
          this.clear()
        }
      } else if (canForceAssociation === 'yes') {
        this.setState(
          { forceAssociation: true, pin: '', encodingCreateCounter: encodingCreateCounter + 1 },
          this.create
        )
      } else {
        this.clear()
      }
    } else {
      this.clear()
    }
  }

  clear = () => {
    if (this.isIdentifierFlow()) {
      this.onClear()
    } else {
      this.clearReads()
    }
  }

  onProductClear = async (addtags = false) => {
    const formSchemaType = this.getInitialTypeFormSchema()
    if (this.initialType === 'order' || formSchemaType.order) {
      this.onClear(addtags, true)
    } else {
      this.onClear(addtags)
    }
  }

  onClear = async (addtags = false, maintainProductionOrder = false) => {
    if (isPreEncodingWorkOrder(this.operation) && this.operation.options?.workOrderRowSelectionMode === 'sizeGrid') {
      this.clearReads(false)
      return
    }
    const { encodingValidation } = this.state
    this.clearTimer()

    this.datadogSession = undefined
    if (this.isIdentifierFlow()) {
      this.clearTimerTags()
      this.clearTimerNoReads()
      await this.startAntenna()
    } else {
      await this.stopAntenna()
    }
    RfidReader.clear()
    if (addtags && encodingValidation?.identifiers) {
      RfidReader.tags = encodingValidation.identifiers
        .filter((idf) => !!idf.code)
        .map(
          (idf) =>
            ({
              uid: idf.identifierType === 'NFC_TAG' && idf.code,
              // usa l'EPC scritto (desiredCode) per la deduplica, vedi addRfidReaderTags
              epc: idf.identifierType === 'UHF_TAG' && (idf.desiredCode || idf.code),
              tid: idf.tid || ''
            }) as any
        )
    }

    const formSchema: any = this.getInitFormSchemaData()
    const formSchemaType = this.getInitialTypeFormSchema()
    if ((this.initialType === 'order' || formSchemaType.order) && maintainProductionOrder) {
      formSchema.productionOrder = this.state.formSchemaData.productionOrder
    }
    this.findOrderTags = []
    this.setState(
      {
        formSchemaData: formSchema,
        tagsRead: [],
        orderFound: undefined,
        associationStatus: 'TO_BE_READ',
        pin: '',
        forceAssociation: false,
        encodingCreateCounter: 0,
        processing: false,
        currentTagType: this.tagTypesToCheck[0]
      },
      this.resetValidateResponse
    )
    this.isEncoding = false
    this.isDecodeNfc = false
  }

  afterClearReads() {
    //extensible
  }

  retry = async () => {
    const { currentTagType, encodingValidation, encodingValidationResponse } = this.state
    if (currentTagType !== 'ALL' && currentTagType !== this.tagTypesToCheck[0]) {
      await this.stopAntenna()
    }

    this.trackDatadogAction('clicOnRetryButton')

    this.clearTimer()
    RfidReader.clear()

    this.isEncoding = false
    this.isDecodeNfc = false

    const identifiers = EncodingExtensions.resetIdentifiersEncodingValition(
      this.operation,
      encodingValidationResponse,
      encodingValidation
    )
    this.findOrderTags = []
    this.setState(
      (prevState) => ({
        encodingValidation: prevState.encodingValidation
          ? { ...prevState.encodingValidation, identifiers }
          : prevState.encodingValidation,
        encodingCreateResponse: undefined,
        associationStatus: 'TO_BE_READ',
        tagsRead: [],
        orderFound: undefined,
        pin: '',
        forceAssociation: false,
        encodingCreateCounter: 0,
        numWriteAttempt: 0,
        writeRetryCount: 0,
        targetTag: '',
        targetTid: '',
        tagToWrite: '',
        serial: '',
        processing: false,
        currentTagType: this.tagTypesToCheck[0]
      }),
      this.startAntenna
    )
    this.afterClearReads()
  }

  addRfidReaderTags = (addtags = false) => {
    const { encodingValidation } = this.state
    if (addtags && encodingValidation?.identifiers) {
      RfidReader.tags = encodingValidation.identifiers
        .filter((idf) => !!idf.code)
        .map(
          (idf) =>
            ({
              uid: idf.identifierType === 'NFC_TAG' && idf.code,
              // dopo la scrittura UHF il tag fisico emette il nuovo EPC (desiredCode): si usa quello per
              // la deduplica del reader, così al riavvio l'antenna non rilegge il tag appena scritto
              epc: idf.identifierType === 'UHF_TAG' && (idf.desiredCode || idf.code),
              tid: idf.tid || ''
            }) as any
        )
    }
  }

  resetDataDogSession = () => {
    if (this.datadogSession) {
      this.antennaStartTime = undefined
      this.datadogSession.startTime = new Date()
      this.datadogSession.errors = []
      this.deleteDatadogSessionProperties([
        'uhfRead',
        'nfcRead',
        'startAntennaRequested',
        'allTagsValidated',
        'operationFailedOffsetMs',
        'additionalInfo',
        'deviceManager',
        'encodingRequested',
        'writeTag'
      ])
    }
  }

  deleteDatadogSessionProperties = (properties: string[]) => {
    if (!this.datadogSession) return
    properties.forEach((prop) => {
      if (this.datadogSession[prop]) {
        delete this.datadogSession[prop]
      }
    })
  }

  clearReads = async (addtags = false, noask = false) => {
    const { resetFormCounter, counters, currentTagType } = this.state
    // stop reading before clearing tags to avoid double readings
    if (
      (getAskUserInputEveryTime(this.operation) && !noask) ||
      (currentTagType !== 'ALL' && currentTagType !== this.tagTypesToCheck[0])
    ) {
      await this.stopAntenna()
    }

    this.totalWriteTagTime = undefined
    this.clearTimer()
    RfidReader.clear()
    this.addRfidReaderTags(addtags)

    if (getAskUserInputEveryTime(this.operation) && !noask) {
      const formSchema: any = this.getInitFormSchemaData()
      const formSchemaType = this.getInitialTypeFormSchema()
      if (
        (this.initialType === 'order' || formSchemaType.order) &&
        getEncodedOrder(counters) < counters.order.expected + counters.order.extra
      ) {
        formSchema.productionOrder = this.state.formSchemaData.productionOrder
      }

      this.datadogSession = undefined
      this.antennaStartTime = undefined
      RfidReader.setAutomaticStop(false)
      await this.stopAntenna()
      this.findOrderTags = []
      this.setState(
        {
          formSchemaData: formSchema,
          associationStatus: 'TO_BE_READ',
          tagsRead: [],
          orderFound: undefined,
          resetFormCounter: resetFormCounter + 1,
          pin: '',
          forceAssociation: false,
          encodingCreateCounter: 0,
          numWriteAttempt: 0,
          writeRetryCount: 0,
          targetTag: '',
          targetTid: '',
          tagToWrite: '',
          serial: '',
          processing: false,
          currentTagType: this.tagTypesToCheck[0]
        },
        this.resetValidateResponse
      )
    } else {
      this.resetDataDogSession()

      if (isPreEncodingWorkOrder(this.operation) && this.operation.options?.workOrderRowSelectionMode === 'sizeGrid') {
        const { formSchemaData } = this.state
        this.encodingFormRef.current?.resetSelectedWorkOrderRowId()

        let refreshCounters
        try {
          if (formSchemaData.workOrderId) {
            const res = await Encodings.getWorkOrderCounter(formSchemaData.workOrderId)
            refreshCounters = res?.data
          }
        } catch {}

        this.setState((prevState) => ({
          encodingValidation: prevState.encodingValidation
            ? { ...prevState.encodingValidation, identifiers: undefined }
            : prevState.encodingValidation,
          encodingCreateResponse: undefined,
          associationStatus: 'TO_BE_READ',
          tagsRead: [],
          orderFound: undefined,
          pin: '',
          forceAssociation: false,
          encodingCreateCounter: 0,
          numWriteAttempt: 0,
          writeRetryCount: 0,
          targetTag: '',
          targetTid: '',
          tagToWrite: '',
          serial: '',
          processing: false,
          counters: refreshCounters,
          currentTagType: this.tagTypesToCheck[0]
        }))
      } else {
        //ricalcolare in base alla configurazione il reset
        const identifiers = EncodingExtensions.resetIdentifiersEncodingValition(
          this.operation,
          this.state.encodingValidationResponse,
          this.state.encodingValidation
        )
        this.findOrderTags = []

        this.setState(
          (prevState) => ({
            encodingValidation: prevState.encodingValidation
              ? { ...prevState.encodingValidation, identifiers }
              : prevState.encodingValidation,
            encodingCreateResponse: undefined,
            associationStatus: 'TO_BE_READ',
            tagsRead: [],
            orderFound: undefined,
            pin: '',
            forceAssociation: false,
            encodingCreateCounter: 0,
            numWriteAttempt: 0,
            writeRetryCount: 0,
            targetTag: '',
            targetTid: '',
            tagToWrite: '',
            serial: '',
            processing: false,
            currentTagType: this.tagTypesToCheck[0]
          }),
          this.startAntenna
        )
      }
    }
    this.isEncoding = false
    this.afterClearReads()
  }

  resetValidateResponse = () => {
    const { formSchemaData, counters: oldCounters } = this.state
    let counters: EncodingCounters | undefined = undefined
    if (formSchemaData.productionOrder) {
      counters = { ...oldCounters, row: undefined }
    }
    this.setState((prevState) => ({
      encodingValidation: undefined,
      encodingValidationResponse: undefined,
      counters,
      encodingCreateResponse: undefined,
      resetFormCounter: prevState.resetFormCounter + 1
    }))
  }

  managerPinModal = () => {
    this.setState((prevState) => ({
      showPin: !prevState.showPin
    }))
  }

  forceWithPin = (pin) => {
    this.setState({ pin: pin, forceAssociation: true, showPin: false }, this.create)
  }

  onTabClick = async (tab) => {
    if (tab === 'verify' && !this.isEncoding) {
      this.setState({ processing: true }, this._changeTab)
    }
  }

  _changeTab = async () => {
    RfidReader.clear()
    await this.stopAntenna()
    setTimeout(() => {
      Router.navigate(`/encoding/:opCode/verify`, { opCode: this.operation.code })
    }, this.operation.options.antennaTurnWaitingInterval ?? 1000)
  }

  getCustomHeader(): React.ReactElement | null {
    if (
      this.operation.options.showAdvancedPoCounters === 'true' &&
      (['order', 'orderRow', 'identifierOrderRow'].includes(this.initialType) ||
        (this.initialType === 'formSchema' &&
          this.operation.formSchema?.some((el) => el.initialType === 'order' || el.initialType === 'orderRow')))
    ) {
      return (
        <Button
          style={{ marginRight: 10, borderRadius: 15 }}
          variant="primary"
          type="button"
          onClick={() => this.showUserCounters()}
        >
          {__(T.misc.activity)}
        </Button>
      )
    }
    return null
  }

  getDisableRetry() {
    return false
  }

  getAssociationMessage(): string | undefined {
    return undefined
  }

  getAssociationButtons(): { text: string; onClick: () => void }[] | undefined {
    return undefined
  }

  getTagWritePassword = () => getEncodingTagPassword(this.operation)

  getDisableInput() {
    return false
  }

  getProcessingMessage() {
    return __(this.state.processing === 'nfcDecryption' ? T.messages.nfcDecryption : T.messages.operation_in_progress)
  }

  getFullWidthAssociationBox() {
    return false
  }

  getHideIdentifierCodeByType = (): IdentifierType[] | undefined => {
    return undefined
  }

  getIdentifierCodeForOrder = () => {
    const { encodingValidation } = this.state
    let code = undefined
    if (this.initialType === 'identifierOrderRow' && encodingValidation?.identifiers) {
      code = encodingValidation.identifiers.find((t) => t.identifierType === 'UHF_TAG' && t.code)?.code ?? undefined
    }
    return code
  }

  showProductionOrderDetail = () => {
    if (this.hasProductionOrder()) {
      this.setState({ showProductionOrder: true })
    }
  }

  closeProductionOrderDetail = () => {
    this.setState({ showProductionOrder: false })
  }

  hasProductionOrder = () => {
    const { formSchemaData, orderFound } = this.state
    return (
      formSchemaData.productionOrder ||
      formSchemaData.productionOrderRow?.productionOrder ||
      orderFound?.productionOrder
    )
  }

  getProductionOrder = () => {
    const { formSchemaData, orderFound } = this.state
    if (formSchemaData.productionOrderRow) {
      return formSchemaData.productionOrderRow.productionOrder ?? {}
    }
    return formSchemaData.productionOrder ?? orderFound?.productionOrder ?? {}
  }

  getCommonUserSettings: () => OperationUserSetting[] | undefined = () => [
    { name: 'enableOkSounds', type: 'boolean', visible: true, default: true },
    { name: 'enableKoSounds', type: 'boolean', visible: true, default: true }
  ]

  handleUserSettingChanged = (name: string, newValue: string | number | boolean) => {
    switch (name) {
      case 'enableOkSounds':
        this.setState({ enableOkSounds: newValue as boolean })
        break
      case 'enableKoSounds':
        this.setState({ enableKoSounds: newValue as boolean })
        break
    }
  }

  getCustomSidebarBottomComponent = () => {
    return null
  }

  onResetDailyEncodedItems = undefined

  showUserCounters = () => {
    this.setState({ showUserCounters: true })
  }

  closeUserCounters = () => {
    this.setState({ showUserCounters: false })
  }

  render() {
    const {
      encodingValidation,
      showPin,
      formSchemaData,
      encodingValidationResponse,
      counters,
      processing,
      associationStatus,
      antennaOn,
      starting,
      resetFormCounter,
      currentTagType,
      showProductionOrder,
      orderFound,
      showUserCounters
    } = this.state
    const identifiers = encodingValidation?.identifiers
    const scanIdentifierConfig = getScanIdentifierConfig(this.operation)
    const skipValidation = isSkipValidation(this.operation)
    const disabledClear = associationStatus === 'WRITING'
    const identifierByTagType = identifiers?.filter(
      (idt) =>
        idt.code ||
        ['ERROR'].includes(idt._status) ||
        (currentTagType === 'NFC' && idt.identifierType === 'NFC_TAG') ||
        (currentTagType === 'UHF' && idt.identifierType === 'UHF_TAG') ||
        currentTagType === 'ALL'
    )
    const pageLayout = getEncodingPageLayout()

    return (
      <Page
        headerRight={
          <EncodingRightHeader
            onTabClick={this.onTabClick}
            antennaOn={antennaOn}
            antennaTagType={currentTagType}
            operation={this.operation}
            options={this.state.options}
            onResetDailyEncodedItems={this.onResetDailyEncodedItems}
          >
            {this.getCustomHeader()}
          </EncodingRightHeader>
        }
        title={this.operation.description}
        enableEmulation
        activeAntennaSettings
        userSettings={{
          operation: this.operation,
          settings: [...(this.getCommonUserSettings() ?? []), ...this.operation.userSettings]
        }}
        onUserSettingChanged={(name, newValue) => this.handleUserSettingChanged(name, newValue)}
      >
        {this.isIdentifierFlow() && (
          <>
            {encodingValidation && encodingValidationResponse && (
              <Page.Sidebar width={pageLayout.sideBarWidth}>
                <EncodingProduct
                  product={EncodingExtensions.getEncodingProduct(encodingValidationResponse)}
                  clear={this.onClear}
                  fields={getEncodingProductFields()}
                  code={this.getIdentifierCodeForOrder()}
                  counters={counters}
                  productionOrderDetail={this.hasProductionOrder() ? this.showProductionOrderDetail : undefined}
                  initialType={this.initialType}
                  operationId={this.operation.id}
                  order={orderFound}
                />
                {this.getCustomSidebarBottomComponent()}
              </Page.Sidebar>
            )}
            <Page.Content notBoxed>
              {(!encodingValidation || !encodingValidationResponse) && (
                <EncodingReading
                  starting={starting}
                  startAntenna={this.startAntenna}
                  antennaOn={antennaOn}
                  message={
                    antennaOn
                      ? __(T.messages.bring_an_item_to_antenna)
                      : __(T.messages.connection_to_station_in_progress)
                  }
                  disableAntenna={!scanIdentifierConfig.rfid}
                  identifier={
                    scanIdentifierConfig.serial && !skipValidation
                      ? {
                          identifierType: 'SIMPLE_ITEM_IDENTIFIER',
                          _status: 'TO_BE_READ'
                        }
                      : undefined
                  }
                  onInputSubmit={
                    scanIdentifierConfig.serial && !skipValidation
                      ? (barcode) => this.verifySerial({ barcode } as any)
                      : undefined
                  }
                />
              )}
              {encodingValidation && encodingValidationResponse && (
                <EncodingIdentifers
                  identifiers={identifiers}
                  clearReads={this.clear} //this.checkForceAssociation
                  onTagRead={this.onTagReadIdentifier}
                  associationStatus={associationStatus}
                  disabledRetry={false}
                />
              )}
            </Page.Content>
          </>
        )}
        {!this.isIdentifierFlow() && (
          <>
            <Page.Sidebar width={pageLayout.sideBarWidth}>
              <EncodingForm
                ref={this.encodingFormRef}
                key={resetFormCounter}
                product={EncodingExtensions.getEncodingProduct(encodingValidationResponse)}
                data={formSchemaData}
                operation={this.operation}
                onSubmit={this.onSubmitForm}
                onProductionOrderReceived={this.onProductionOrderReceived}
                onWorkOrderReceived={this.onWorkOrderReceived}
                onWorkOrderDatadogEvent={this.onWorkOrderDatadogEvent}
                updateFormSchemaData={this.updateFormSchemaData}
                onProductionOrderClear={this.onClear}
                onWorkOrderClear={this.onClear}
                onProductClear={this.onProductClear}
                onIdentifierClear={this.clearReads}
                counters={counters}
                resetStateCounter={resetFormCounter}
                disabledClear={disabledClear}
                disableInput={this.getDisableInput()}
                overrideProductInfoFields={EncodingExtensions.getOverrideProductInfoFields(
                  'encodingForm',
                  this.operation
                )}
                productionOrderDetail={this.hasProductionOrder() ? this.showProductionOrderDetail : undefined}
                enableKoSounds={this.state.enableKoSounds}
              />
              {this.getCustomSidebarBottomComponent()}
            </Page.Sidebar>
            <Page.Content notBoxed>
              {identifiers && encodingValidation && (
                <EncodingIdentifers
                  identifiers={identifierByTagType}
                  clearReads={this.retry} //this.checkForceAssociation
                  onTagRead={this.onTagRead}
                  associationStatus={associationStatus}
                  disabledRetry={this.getDisableRetry()}
                  fullWidthAssociationBox={this.getFullWidthAssociationBox()}
                  hideIdentifierCodeByType={this.getHideIdentifierCodeByType()}
                  associationMessage={this.getAssociationMessage()}
                  associationButtons={this.getAssociationButtons()}
                />
              )}
              {!identifiers && (
                <Box borderRadius={15} ph={40} flex bg="white" center>
                  <Text center style={{ width: 500 }} fontSize={30}>
                    {__(T.messages.fill_all_fields_and_search_an_item_configuration)}
                  </Text>
                </Box>
              )}
            </Page.Content>
          </>
        )}
        {processing && <FullLoadingLayer message={this.getProcessingMessage()} />}

        {showPin && (
          <InputModal
            title={__(T.titles.pin_modal)}
            labelInput="Pin"
            onClose={() => this.managerPinModal()}
            onConfirm={this.forceWithPin}
          />
        )}

        {!AppStore.hasAnyCapabilities('Operation.Encoding.perform') && (
          <FeedbackModal
            title={__('error.unauthorized')}
            subtitle={__('messages.not_allowed_operation')}
            onClose={() => Router.navigate('/')}
            btnCloseModal={__('misc.back')}
          />
        )}

        {showProductionOrder && (
          <EncodingProductionOrderDetail
            productionOrder={this.getProductionOrder()}
            onClose={this.closeProductionOrderDetail}
          />
        )}

        {showUserCounters && <EncodingUserCounters onClose={this.closeUserCounters} />}
      </Page>
    )
  }
}
